/**
 * Typed wrappers for the read endpoints wired in MVP plus accept-invitation.
 *
 * Mutation endpoints are intentionally NOT exposed here — Task 038 ships
 * read-only screens; mutations are deferred to follow-up tasks per
 * `docs/frontend/035-frontend-app-shell-plan.md §1.2`.
 */

import { kmiFetch, kmiFetchBinary } from './client';
import type {
  // Task 298 (Foundation F) — organization commercial standing.
  CommercialAction,
  CommercialState,
  ListOrganizationCommercialStateData,
  OrganizationActivationStatusData,
  SetOrganizationCommercialStateData,
  // Task 306A — the operator's per-organization detail read.
  OrganizationCommercialDetailData,
  // Task 307A — list sort + manual SaaS plan assignment.
  CommercialListSort,
  SetOrganizationSubscriptionPlanData,
  GetAgencyProfileData,
  UpsertAgencyProfileData,
  UpsertAgencyProfileInput,
  // Task 301 (Lane B) — agency SaaS subscription commercial summary.
  AgencyCommercialSummaryData,
  // Task 303-A — the agency-client contract (externally executed PDF).
  ClientContractsOverviewData,
  CreateAgencyClientContractData,
  CreateAgencyClientContractInput,
  CreateContractUploadData,
  CreateContractUploadInput,
  CompleteContractUploadData,
  CompleteContractUploadInput,
  ReleaseContractVersionData,
  SetContractArchiveStateData,
  SetContractArchiveStateInput,
  ContractDocumentDownload,
  // Task 303-B — client acknowledgement of an externally executed version.
  AcknowledgeContractVersionData,
  AcknowledgeContractVersionInput,
  AcceptInvitationData,
  ContentWriterDraftStatus,
  ListContentWriterDraftsData,
  ReviewContentWriterDraftData,
  ReviewContentWriterDraftInput,
  SaveContentWriterDraftData,
  SaveContentWriterDraftInput,
  ListEligibleContentWritersData,
  SendContentItemToWritingData,
  SendContentItemToWritingInput,
  SubmitContentWriterDraftData,
  SubmitContentWriterDraftInput,
  SignupAgencyData,
  SignupAgencyInitiateData,
  AiPromptBlockStatus,
  BootstrapAgencyServicesData,
  CompleteCreativeAssetUploadData,
  CompleteCreativeAssetUploadInput,
  CreateAiPromptBlockData,
  CreateAiPromptBlockInput,
  CreateCreativeAssetUploadData,
  CreateCreativeAssetUploadInput,
  ClientOverviewData,
  ContentDiscipline,
  ContentItemStatus,
  CreateClientData,
  CreateClientGoalData,
  CreateClientGoalInput,
  CreateClientInitiativeData,
  CreateClientInitiativeInput,
  CreateClientInput,
  ApplyClientOnboardingSuggestionData,
  ApplyClientOnboardingSuggestionInput,
  CreateClientStrategyFromOnboardingData,
  CreateClientTargetAudienceData,
  CreateClientTargetAudienceInput,
  ExtractClientOnboardingFromWebsiteResponse,
  GetClientOnboardingExtractionStatusData,
  GenerateClientStrategyAiInput,
  GenerateClientStrategyAiStartData,
  GetStrategyGenerationStatusData,
  RefineClientStrategySectionData,
  RefineClientStrategySectionInput,
  RefineClientStrategySectionPreviewData,
  RefineClientStrategySectionPreviewInput,
  RefineClientStrategySectionPublishData,
  RefineClientStrategySectionPublishInput,
  PublishClientStrategyVersionData,
  PublishClientStrategyVersionInput,
  GetClientOnboardingProfileData,
  CrmDealStage,
  CrmLeadStatus,
  GetClientStrategyData,
  GetContentItemData,
  GetCreativeAnalysisStatusData,
  GetCrmDealData,
  GetCrmLeadData,
  GetCreativeAssetData,
  GetInfluencerData,
  GetPaidMediaCampaignData,
  GetReportJobData,
  ListAdAccountsData,
  ListClientInitiativesData,
  ListClientStrategiesData,
  ListClientsData,
  ListClientTeamData,
  AssignClientTeamMemberData,
  AssignClientTeamMemberInput,
  EndClientTeamAssignmentData,
  EndClientTeamAssignmentInput,
  ListClientTeamPermissionsData,
  GrantClientTeamPermissionData,
  GrantClientTeamPermissionInput,
  RevokeClientTeamPermissionData,
  RevokeClientTeamPermissionInput,
  ListPermissionsData,
  GenerateContentCalendarDraftData,
  GenerateContentCalendarDraftInput,
  ClientMonthlyReportData,
  // Task 309A — organic content performance.
  ClientOrganicPerformanceData,
  OrganicPeriodKindWire,
  OrganicPlatformWire,
  ListCalendarEntriesByMonthData,
  ListContentApprovalsData,
  ListContentItemsData,
  ListContentVariantsData,
  ListPendingClientApprovalsData,
  PendingClientApprovalStage,
  SaveContentCalendarDraftData,
  SaveContentCalendarDraftInput,
  GenerateWriterAiDraftsData,
  GenerateWriterAiDraftsInput,
  SaveWriterAiCalendarDraftData,
  SaveWriterAiCalendarDraftInput,
  TransitionContentItemStatusData,
  TransitionContentItemStatusInput,
  PrepareProductionTaskUploadData,
  PrepareProductionTaskUploadInput,
  StartProductionTaskData,
  StartProductionTaskInput,
  SubmitProductionTaskData,
  SubmitProductionTaskInput,
  BlockProductionTaskData,
  BlockProductionTaskInput,
  RequestProductionTaskClarificationData,
  RequestProductionTaskClarificationInput,
  ListProductionTasksForContentItemData,
  AssignProductionTaskData,
  AssignProductionTaskInput,
  ListEligibleProductionAssigneesData,
  ListMyProductionTasksData,
  UpdateContentCalendarDraftData,
  UpdateContentCalendarDraftInput,
  ListCreativeAssetMatchesData,
  ListCreativeAssetsData,
  ListCreativeIntelligenceContentGroupsData,
  SuggestCreativeAssetPaidMatchesData,
  SuggestCreativeAssetPaidMatchesParams,
  CreateCreativeAssetMatchData,
  CreateCreativeAssetMatchInput,
  ListCrmDealActivitiesData,
  ListCrmDealStageHistoryData,
  ListCrmDealsData,
  ListCrmLeadsData,
  ListInfluencerPlatformAccountsData,
  ListInfluencersData,
  ListNotificationsData,
  ListPaidMediaAdSetsData,
  ListPaidMediaAdsData,
  ListPaidMediaCampaignsData,
  ListClientPaidCampaignsData,
  ListPaidMediaMetricsData,
  PaidMediaPerformanceSummaryData,
  SyncPaidMediaMetaCampaignsInput,
  PaidMediaSyncSummaryData,
  SnapchatPaidMediaSyncSummaryData,
  ListReportArtifactsData,
  ListReportJobsData,
  ListRolesData,
  ListPublishJobsData,
  ListSocialConnectionsData,
  StartSocialConnectionOAuthInput,
  StartSocialConnectionOAuthData,
  DisconnectSocialConnectionInput,
  DisconnectSocialConnectionData,
  ListStrategySuggestionsData,
  ListUserInvitationsData,
  ScheduleContentItemPublishData,
  ScheduleContentItemPublishInput,
  MarkPublishJobPublishedData,
  MarkPublishJobPublishedInput,
  MarkPublishJobFailedData,
  MarkPublishJobFailedInput,
  CancelPublishJobData,
  CancelPublishJobInput,
  PublishJobStatus,
  ListUserRolesData,
  MeContext,
  PaidMediaEntityStatus,
  PaidMediaMetricLevel,
  PaidMediaPlatform,
  RevokeUserInvitationData,
  RevokeUserInvitationInput,
  ReportJobStatus,
  RoleCategory,
  SendUserInvitationData,
  SendUserInvitationInput,
  UpdateClientData,
  UpdateClientInput,
  UpsertClientBrandInfoData,
  UpsertClientBrandInfoInput,
  GetClientOperatingSetupData,
  UpsertClientOperatingSetupData,
  UpsertClientOperatingSetupInput,
  UpsertClientOnboardingProfileData,
  UpsertClientOnboardingProfileInput,
  AssignClientPackageData,
  AssignClientPackageInput,
  UpdateClientPackageAssignmentInput,
  UpdateClientPackageAssignmentData,
  GetClientPackageProgressData,
  RecordPackageServiceDeliveryData,
  RecordPackageServiceDeliveryInput,
  ListAgencyPackagesData,
  ListAgencyServicesData,
  ListAiPromptBlocksData,
  ListAiPromptBlocksParams,
  ArchiveContentCreativeAssetData,
  ArchiveContentCreativeAssetInput,
  ListContentCreativeAssetsData,
  ListContentCreativeAssetsParams,
  TransitionAiPromptBlockStatusData,
  TransitionAiPromptBlockStatusInput,
  UpdateAiPromptBlockData,
  UpdateAiPromptBlockInput,
  ListClientPackageAssignmentsData,
  SetAgencyPackageServicesData,
  SetAgencyPackageServicesInput,
  UpsertAgencyPackageData,
  UpsertAgencyPackageInput,
  UpsertAgencyServiceData,
  UpsertAgencyServiceInput,
  // Task 141 — IC-3 paid-media OAuth wire shapes.
  CompleteIntegrationConnectionOAuthData,
  CompleteIntegrationConnectionOAuthFirstCallInput,
  CompleteIntegrationConnectionOAuthSelectInput,
  StartIntegrationConnectionOAuthData,
  StartIntegrationConnectionOAuthInput,
  CreateCiAssetUploadsInput,
  CreateCiAssetUploadsData,
  CompleteCiAssetUploadsInput,
  CompleteCiAssetUploadsData,
} from './types';

export interface FetchSignal {
  readonly signal?: AbortSignal;
}

// ── shell + four MVP wires ────────────────────────────────────────────

export function getMeContext(opts: FetchSignal = {}): Promise<MeContext> {
  return kmiFetch<MeContext>('me-context', { method: 'GET', signal: opts.signal });
}

export interface ListClientsParams extends FetchSignal {
  /**
   * Task hide-archived-clients — opt-in to include `status='archived'`
   * clients in the response. Default `false` (UI hides archived from
   * normal lists). Passed to the EF as `?include_archived=true` —
   * the EF treats any value other than the literal string `"true"`
   * as false.
   */
  readonly includeArchived?: boolean;
}

export function listClients(opts: ListClientsParams = {}): Promise<ListClientsData> {
  return kmiFetch<ListClientsData>('list-clients', {
    method: 'GET',
    signal: opts.signal,
    query: opts.includeArchived ? { include_archived: 'true' } : undefined,
  });
}

export interface CreateClientOptions extends FetchSignal {
  readonly input: CreateClientInput;
}

/**
 * Create a new client row in the actor's organization. The Edge Function
 * `create-client` (Task 043) is the first authenticated write — auth runs
 * before body validation, RLS on `clients_insert` is the load-bearing
 * gate, and the wire response is whitelisted to the same shape as
 * `list-clients` so the caller can append the row to local state without
 * a refetch.
 *
 * Wrapper name: `createClient` (no conflict with `@supabase/supabase-js`'s
 * `createClient` because the supabase client lives in `../auth/supabase`
 * and is imported as the singleton `supabase` everywhere in this app).
 */
export function createClient(options: CreateClientOptions): Promise<CreateClientData> {
  return kmiFetch<CreateClientData>('create-client', {
    method: 'POST',
    signal: options.signal,
    body: {
      name: options.input.name,
      status: options.input.status,
      notes: options.input.notes ?? null,
    },
  });
}

// ── client setup writes (Task 044) ─────────────────────────────────────

export interface UpdateClientOptions extends FetchSignal {
  readonly input: UpdateClientInput;
}

/**
 * Patch a client's name / status / notes. Used by the Overview tab's
 * "إنهاء الإعداد وتفعيل العميل" flow (status onboarding → active) and
 * inline edits. Backend gate is `client.manage` OR `client.self.manage`;
 * RLS on `clients_update` is the load-bearing guard.
 */
export function updateClient(options: UpdateClientOptions): Promise<UpdateClientData> {
  return kmiFetch<UpdateClientData>('update-client', {
    method: 'PATCH',
    signal: options.signal,
    body: {
      client_id: options.input.client_id,
      name: options.input.name,
      status: options.input.status,
      notes: options.input.notes,
    },
  });
}

export interface UpsertClientBrandInfoOptions extends FetchSignal {
  readonly input: UpsertClientBrandInfoInput;
}

/**
 * Upsert (insert or replace) the single `brand_info` row for a client.
 * The unique `(client_id)` constraint makes this idempotent — a second
 * call with the same `client_id` overwrites the row's data. Backend
 * uses PostgREST's `Prefer: resolution=merge-duplicates`.
 */
export function upsertClientBrandInfo(
  options: UpsertClientBrandInfoOptions,
): Promise<UpsertClientBrandInfoData> {
  return kmiFetch<UpsertClientBrandInfoData>('upsert-client-brand-info', {
    method: 'POST',
    signal: options.signal,
    body: {
      client_id: options.input.client_id,
      brand_name: options.input.brand_name,
      industry: options.input.industry ?? null,
      website_url: options.input.website_url ?? null,
      primary_locale: options.input.primary_locale ?? null,
      notes: options.input.notes ?? null,
    },
  });
}

/**
 * Task 279 — load the single `client_operating_setup` row for a client, or
 * `{ operating_setup: null }` when none exists yet. RLS-gated (agency +
 * assigned client-category users can read).
 */
export function getClientOperatingSetup(
  clientId: string,
  opts: FetchSignal = {},
): Promise<GetClientOperatingSetupData> {
  return kmiFetch<GetClientOperatingSetupData>('get-client-operating-setup', {
    method: 'GET',
    query: { client_id: clientId },
    signal: opts.signal,
  });
}

export interface UpsertClientOperatingSetupOptions extends FetchSignal {
  readonly input: UpsertClientOperatingSetupInput;
}

/**
 * Task 279 — upsert (insert or full-replace) the single
 * `client_operating_setup` row for a client (PK = client_id; backend uses
 * `Prefer: resolution=merge-duplicates`). AGENCY-ONLY write — RLS requires
 * `client.manage` + an agency role; client-category users are read-only.
 */
export function upsertClientOperatingSetup(
  options: UpsertClientOperatingSetupOptions,
): Promise<UpsertClientOperatingSetupData> {
  return kmiFetch<UpsertClientOperatingSetupData>('upsert-client-operating-setup', {
    method: 'POST',
    signal: options.signal,
    body: {
      client_id: options.input.client_id,
      posts_per_week: options.input.posts_per_week ?? null,
      static_designs_per_week: options.input.static_designs_per_week ?? null,
      reels_videos_per_week: options.input.reels_videos_per_week ?? null,
      platforms: options.input.platforms ?? null,
      preferred_days: options.input.preferred_days ?? null,
      preferred_times_local: options.input.preferred_times_local ?? null,
      timezone: options.input.timezone ?? null,
      first_publish_date: options.input.first_publish_date ?? null,
      production_lead_time_days: options.input.production_lead_time_days ?? 0,
      approval_buffer_days: options.input.approval_buffer_days ?? 0,
      is_active: options.input.is_active ?? true,
    },
  });
}

export interface CreateClientTargetAudienceOptions extends FetchSignal {
  readonly input: CreateClientTargetAudienceInput;
}

/**
 * Create a new target audience for a client. UPDATE / DELETE are
 * intentionally not exposed in V1 (Task 044 spec); soft-delete via
 * `ended_at` is for a later flow.
 */
export function createClientTargetAudience(
  options: CreateClientTargetAudienceOptions,
): Promise<CreateClientTargetAudienceData> {
  return kmiFetch<CreateClientTargetAudienceData>('create-client-target-audience', {
    method: 'POST',
    signal: options.signal,
    body: {
      client_id: options.input.client_id,
      label: options.input.label,
      description: options.input.description ?? null,
      is_primary: options.input.is_primary ?? false,
    },
  });
}

export interface CreateClientGoalOptions extends FetchSignal {
  readonly input: CreateClientGoalInput;
}

/** Create a new client goal. UPDATE / DELETE deferred per Task 044 spec. */
export function createClientGoal(options: CreateClientGoalOptions): Promise<CreateClientGoalData> {
  return kmiFetch<CreateClientGoalData>('create-client-goal', {
    method: 'POST',
    signal: options.signal,
    body: {
      client_id: options.input.client_id,
      goal: options.input.goal,
      metric_name: options.input.metric_name ?? null,
      target_value: options.input.target_value ?? null,
    },
  });
}

/**
 * List initiatives for a client. Backend is `list-client-initiatives`
 * (already shipped in an earlier task); this wrapper was simply not
 * wired in MVP. Task 044 wires it for the Overview initiatives card.
 */
export function listClientInitiatives(
  clientId: string,
  opts: FetchSignal = {},
): Promise<ListClientInitiativesData> {
  return kmiFetch<ListClientInitiativesData>('list-client-initiatives', {
    method: 'GET',
    signal: opts.signal,
    query: { client_id: clientId },
  });
}

export interface CreateClientInitiativeOptions extends FetchSignal {
  readonly input: CreateClientInitiativeInput;
}

/**
 * Create a new initiative for a client. Server enforces:
 *   - `organization_id` from the actor's profile (not the body),
 *   - `owner_user_id` defaults to the actor,
 *   - `status` fixed at 'planning' on create,
 *   - `metadata` empty, no completed/cancelled timestamps.
 *
 * Backend gate is `initiative.create` plus an RLS-required role of
 * `owner` / `marketing_manager` / `account_manager`.
 */
export function createClientInitiative(
  options: CreateClientInitiativeOptions,
): Promise<CreateClientInitiativeData> {
  return kmiFetch<CreateClientInitiativeData>('create-client-initiative', {
    method: 'POST',
    signal: options.signal,
    body: {
      client_id: options.input.client_id,
      name: options.input.name,
      description: options.input.description ?? null,
      start_date: options.input.start_date ?? null,
      target_end_date: options.input.target_end_date ?? null,
    },
  });
}

// ── client onboarding profile (Task 045A) ─────────────────────────────

/**
 * Read the client's long-form onboarding profile. Returns
 * `{ onboarding_profile: null }` when the row doesn't exist yet (the
 * wizard's blank-slate empty state). 404 only fires when the parent
 * client itself is invisible / cross-org / non-existent.
 *
 * The companion read for short brand identity remains
 * `getClientOverview` — `BrandInfo` covers brand_name / industry /
 * website_url / primary_locale, while this endpoint covers vision /
 * mission / values / USP / strengths / weaknesses / products /
 * competitors / etc.
 */
export function getClientOnboardingProfile(
  clientId: string,
  opts: FetchSignal = {},
): Promise<GetClientOnboardingProfileData> {
  return kmiFetch<GetClientOnboardingProfileData>('get-client-onboarding-profile', {
    method: 'GET',
    signal: opts.signal,
    query: { client_id: clientId },
  });
}

export interface UpsertClientOnboardingProfileOptions extends FetchSignal {
  readonly input: UpsertClientOnboardingProfileInput;
}

/**
 * Upsert the client's onboarding profile with PATCH semantics. Each
 * key in `input` is optional; absent keys preserve the existing
 * column value, explicit `null` clears a text column, and explicit
 * arrays / objects replace jsonb columns. The runtime branches on
 * existence (SELECT-then-INSERT-or-PATCH) so partial saves don't
 * clobber other sections.
 */
export function upsertClientOnboardingProfile(
  options: UpsertClientOnboardingProfileOptions,
): Promise<UpsertClientOnboardingProfileData> {
  // Build the body with only the keys the caller actually provided so
  // the wire matches the runtime's "absent = preserve" contract.
  const body: Record<string, unknown> = { client_id: options.input.client_id };
  const fields: ReadonlyArray<keyof UpsertClientOnboardingProfileInput> = [
    'arabic_description',
    'english_description',
    'vision',
    'mission',
    'values_text',
    'usp',
    'strengths',
    'weaknesses',
    'offers_notes',
    'internal_notes',
    'products_services',
    'competitors',
    'progress',
    'completed_at',
  ];
  for (const f of fields) {
    if (f in options.input) {
      body[f] = options.input[f] as unknown;
    }
  }
  return kmiFetch<UpsertClientOnboardingProfileData>('upsert-client-onboarding-profile', {
    method: 'POST',
    signal: options.signal,
    body,
  });
}

export function getClientOverview(
  clientId: string,
  opts: FetchSignal = {},
): Promise<ClientOverviewData> {
  return kmiFetch<ClientOverviewData>('get-client-overview', {
    method: 'GET',
    query: { client_id: clientId },
    signal: opts.signal,
  });
}

// ── client team (Task 109) ────────────────────────────────────────────

export interface ListClientTeamParams extends FetchSignal {
  readonly clientId: string;
}

/**
 * Read the agency-employee members of a client's team, each with their
 * global agency role(s) and derived content-discipline eligibility.
 * RLS-bound: leadership / account_manager see the full roster, everyone
 * else sees only their own membership row.
 */
export function listClientTeam(params: ListClientTeamParams): Promise<ListClientTeamData> {
  return kmiFetch<ListClientTeamData>('list-client-team', {
    method: 'GET',
    query: { client_id: params.clientId },
    signal: params.signal,
  });
}

export interface AssignClientTeamMemberOptions extends FetchSignal {
  readonly input: AssignClientTeamMemberInput;
}

/**
 * Add an agency employee to a client's team. Owner / marketing_manager
 * only. A duplicate active assignment returns a 409 `conflict`.
 */
export function assignClientTeamMember(
  options: AssignClientTeamMemberOptions,
): Promise<AssignClientTeamMemberData> {
  return kmiFetch<AssignClientTeamMemberData>('assign-client-team-member', {
    method: 'POST',
    signal: options.signal,
    body: options.input,
  });
}

export interface EndClientTeamAssignmentOptions extends FetchSignal {
  readonly input: EndClientTeamAssignmentInput;
}

/**
 * Soft-remove an agency employee from a client's team (sets `ended_at`).
 * Owner / marketing_manager only. Re-ending an already-ended assignment
 * is an idempotent no-op success (`already_ended=true`).
 *
 * Task 122A: this call also cascades `revoked_at` on every
 * `client_user_permission_overrides` row matching
 * (org, client, target user). The cascade is best-effort server-side
 * and does not change the response shape — callers continue to
 * inspect `already_ended` only.
 */
export function endClientTeamAssignment(
  options: EndClientTeamAssignmentOptions,
): Promise<EndClientTeamAssignmentData> {
  return kmiFetch<EndClientTeamAssignmentData>('end-client-team-assignment', {
    method: 'POST',
    signal: options.signal,
    body: options.input,
  });
}

// ── Task 122A — client-scoped permission overrides ─────────────────

export interface ListClientTeamPermissionsParams extends FetchSignal {
  readonly clientId: string;
  /** Optional filter — narrow to a single target user. */
  readonly userId?: string;
}

/**
 * Read non-revoked client-scoped permission overrides for a client,
 * optionally filtered to one target user. RLS-bound: a non-leadership
 * caller silently sees zero rows (no permission error).
 */
export function listClientTeamPermissions(
  params: ListClientTeamPermissionsParams,
): Promise<ListClientTeamPermissionsData> {
  const query: Record<string, string> = { client_id: params.clientId };
  if (params.userId) query.user_id = params.userId;
  return kmiFetch<ListClientTeamPermissionsData>('list-client-team-permissions', {
    method: 'GET',
    query,
    signal: params.signal,
  });
}

export interface GrantClientTeamPermissionOptions extends FetchSignal {
  readonly input: GrantClientTeamPermissionInput;
}

/**
 * Grant or explicitly deny ONE permission for ONE target user on ONE
 * specific client. Owner / marketing_manager only. Per CSP-1.5 the
 * override row is INERT until a 122B PR adopts `app.has_client_permission`
 * at a specific gate.
 *
 * A duplicate active override for the same (user, client, permission)
 * tuple returns 409 `conflict { detail: 'duplicate_override' }` — a
 * distinct detail from `assign-client-team-member`'s `'duplicate'`
 * (already-on-team) so the FE can map the two to different copy
 * without re-introspecting the EF name.
 */
export function grantClientTeamPermission(
  options: GrantClientTeamPermissionOptions,
): Promise<GrantClientTeamPermissionData> {
  return kmiFetch<GrantClientTeamPermissionData>('grant-client-team-permission', {
    method: 'POST',
    signal: options.signal,
    body: options.input,
  });
}

export interface RevokeClientTeamPermissionOptions extends FetchSignal {
  readonly input: RevokeClientTeamPermissionInput;
}

/**
 * Soft-revoke an active client-scoped permission override (sets
 * `revoked_at` + `revoked_by_user_id`). Owner / marketing_manager only.
 * Re-revoking an already-revoked override is an idempotent no-op
 * success (`already_revoked=true`).
 */
export function revokeClientTeamPermission(
  options: RevokeClientTeamPermissionOptions,
): Promise<RevokeClientTeamPermissionData> {
  return kmiFetch<RevokeClientTeamPermissionData>('revoke-client-team-permission', {
    method: 'POST',
    signal: options.signal,
    body: options.input,
  });
}

/**
 * Read the permissions catalog — the source the FE override-grant form
 * uses to populate its permission picker. Org-agnostic; admits any
 * authenticated reader (RLS on `public.permissions` is open per Task
 * 004 §6.5).
 */
export function listPermissions(params: FetchSignal = {}): Promise<ListPermissionsData> {
  return kmiFetch<ListPermissionsData>('list-permissions', {
    method: 'GET',
    signal: params.signal,
  });
}

export interface ListNotificationsParams extends FetchSignal {
  readonly limit?: number;
  readonly before?: string;
  readonly beforeId?: string;
  readonly unreadOnly?: boolean;
}

export function listNotifications(
  params: ListNotificationsParams = {},
): Promise<ListNotificationsData> {
  return kmiFetch<ListNotificationsData>('list-notifications', {
    method: 'GET',
    signal: params.signal,
    query: {
      limit: params.limit,
      before: params.before,
      before_id: params.beforeId,
      unread_only:
        params.unreadOnly === undefined ? undefined : params.unreadOnly ? 'true' : 'false',
    },
  });
}

// ── strategy ─────────────────────────────────────────────────────────

export interface CreateClientStrategyFromOnboardingOptions extends FetchSignal {
  readonly clientId: string;
}

/**
 * Generate a baseline V1 strategy from the client's onboarding inputs.
 *
 * Wraps `POST /functions/v1/create-client-strategy-from-onboarding`
 * which delegates to the atomic `public.create_client_strategy_v1` RPC
 * (see `supabase/migrations/20260428180000_strategy_create_v1_rpc.sql`).
 * Template-mode generator only — no AI call. Caller must hold both
 * `strategy.create` and `strategy.version.publish` and the client must
 * pass the readiness checks (brand_info + non-empty onboarding profile
 * + ≥1 audience + ≥1 goal); otherwise the endpoint returns a typed
 * `validation_error` with a `fields` map identifying the gap.
 */
export function createClientStrategyFromOnboarding(
  options: CreateClientStrategyFromOnboardingOptions,
): Promise<CreateClientStrategyFromOnboardingData> {
  return kmiFetch<CreateClientStrategyFromOnboardingData>(
    'create-client-strategy-from-onboarding',
    {
      method: 'POST',
      signal: options.signal,
      body: { client_id: options.clientId },
    },
  );
}

export interface PublishClientStrategyVersionOptions extends FetchSignal {
  readonly input: PublishClientStrategyVersionInput;
}

/**
 * Publish a new immutable `strategy_versions` row for an existing
 * strategy and atomically flip `client_strategies.active_version_id`
 * to it. Wraps `POST /functions/v1/publish-client-strategy-version`
 * which delegates to `public.publish_client_strategy_version_v1` (see
 * `supabase/migrations/20260428210000_strategy_publish_version_rpc.sql`).
 *
 * The schema makes `strategy_versions` immutable after insert, so this
 * is the only safe edit shape — old versions are preserved as history.
 *
 * Caller must hold `strategy.version.publish`. Re-checked server-side.
 */
export function publishClientStrategyVersion(
  options: PublishClientStrategyVersionOptions,
): Promise<PublishClientStrategyVersionData> {
  return kmiFetch<PublishClientStrategyVersionData>('publish-client-strategy-version', {
    method: 'POST',
    signal: options.signal,
    body: {
      strategy_id: options.input.strategy_id,
      summary: options.input.summary,
      content: options.input.content,
    },
  });
}

export function listClientStrategies(
  clientId: string | null,
  opts: FetchSignal = {},
): Promise<ListClientStrategiesData> {
  return kmiFetch<ListClientStrategiesData>('list-client-strategies', {
    method: 'GET',
    signal: opts.signal,
    query: clientId ? { client_id: clientId } : {},
  });
}

export function getClientStrategy(
  strategyId: string,
  opts: FetchSignal = {},
): Promise<GetClientStrategyData> {
  return kmiFetch<GetClientStrategyData>('get-client-strategy', {
    method: 'GET',
    signal: opts.signal,
    query: { strategy_id: strategyId },
  });
}

/**
 * Task 260 — client-scoped creative-analysis status read (jobs + persisted
 * analyses) via the Task 227 `get-creative-analysis-status` EF. Read-only,
 * RLS-gated server-side; no AI, no writes.
 */
export function getCreativeAnalysisStatus(
  clientId: string,
  opts: FetchSignal = {},
): Promise<GetCreativeAnalysisStatusData> {
  return kmiFetch<GetCreativeAnalysisStatusData>('get-creative-analysis-status', {
    method: 'GET',
    signal: opts.signal,
    query: { client_id: clientId },
  });
}

export function listStrategySuggestions(
  strategyId: string,
  opts: FetchSignal = {},
): Promise<ListStrategySuggestionsData> {
  return kmiFetch<ListStrategySuggestionsData>('list-strategy-suggestions', {
    method: 'GET',
    signal: opts.signal,
    query: { strategy_id: strategyId },
  });
}

// ── content ──────────────────────────────────────────────────────────

export interface ListContentItemsParams extends FetchSignal {
  readonly clientId?: string;
  readonly status?: ContentItemStatus;
  readonly discipline?: ContentDiscipline;
  readonly limit?: number;
}

export function listContentItems(
  params: ListContentItemsParams = {},
): Promise<ListContentItemsData> {
  return kmiFetch<ListContentItemsData>('list-content-items', {
    method: 'GET',
    signal: params.signal,
    query: {
      client_id: params.clientId,
      status: params.status,
      discipline: params.discipline,
      limit: params.limit,
    },
  });
}

export function getContentItem(id: string, opts: FetchSignal = {}): Promise<GetContentItemData> {
  return kmiFetch<GetContentItemData>('get-content-item', {
    method: 'GET',
    signal: opts.signal,
    query: { id },
  });
}

export function listContentVariants(
  contentItemId: string,
  opts: FetchSignal = {},
): Promise<ListContentVariantsData> {
  return kmiFetch<ListContentVariantsData>('list-content-variants', {
    method: 'GET',
    signal: opts.signal,
    query: { content_item_id: contentItemId },
  });
}

export function listContentApprovals(
  contentItemId: string,
  opts: FetchSignal = {},
): Promise<ListContentApprovalsData> {
  return kmiFetch<ListContentApprovalsData>('list-content-approvals', {
    method: 'GET',
    signal: opts.signal,
    query: { content_item_id: contentItemId },
  });
}

// ── Task 067: AI content calendar draft modes ────────────────────────

export interface GenerateContentCalendarDraftOptions extends FetchSignal {
  readonly input: GenerateContentCalendarDraftInput;
}

/**
 * Generate a draft content calendar via the AI worker. Returns a
 * preview payload (`draft`) the UI lets the user edit before saving
 * through {@link saveContentCalendarDraft}. NO `content_items` /
 * `content_variants` / `calendar_entries` rows are written by this
 * call — only an immutable `ai_runs` row.
 *
 * The endpoint runs in one of two modes:
 *   - `unified`            — one shared caption per idea (cheaper).
 *   - `platform_adapted`   — per-platform copy variants (premium).
 *
 * Caller must hold `ai.run` AND `content.create` AND
 * `content.variant.manage` AND `content.schedule`.
 */
export function generateContentCalendarDraft(
  options: GenerateContentCalendarDraftOptions,
): Promise<GenerateContentCalendarDraftData> {
  return kmiFetch<GenerateContentCalendarDraftData>('generate-content-calendar-draft', {
    method: 'POST',
    signal: options.signal,
    body: options.input,
  });
}

export interface SaveContentCalendarDraftOptions extends FetchSignal {
  readonly input: SaveContentCalendarDraftInput;
}

/**
 * Persist an edited calendar draft as `draft`-status content_items +
 * content_variants + calendar_entries. Atomic via the
 * `public.save_content_calendar_draft_v1` RPC.
 *
 * `origin === 'ai_assisted'` requires `ai_run_id` + `mode`. The
 * server verifies the run belongs to the same org/client + is one of
 * the calendar capabilities + has no `error_class`.
 *
 * `origin === 'manual'` requires `ai_run_id` + `mode` to be absent.
 *
 * Caller must hold `content.create` AND `content.variant.manage` AND
 * `content.schedule`.
 */
export function saveContentCalendarDraft(
  options: SaveContentCalendarDraftOptions,
): Promise<SaveContentCalendarDraftData> {
  return kmiFetch<SaveContentCalendarDraftData>('save-content-calendar-draft', {
    method: 'POST',
    signal: options.signal,
    body: options.input,
  });
}

// ── Task 292C3: writer AI generation ────────────────────────────────

export interface GenerateWriterAiDraftsOptions extends FetchSignal {
  readonly input: GenerateWriterAiDraftsInput;
}

/**
 * Task 292C3 — writer-scoped AI calendar generation.
 *
 * Same worker + same wire shape as {@link generateContentCalendarDraft}.
 * The DIFFERENCE is authorization: the caller must be an active agency
 * `content_writer` with a live non-archived assignment to an ACTIVE
 * client. The server re-derives every predicate; the FE is a surface,
 * not a gate.
 */
export function generateWriterAiDrafts(
  options: GenerateWriterAiDraftsOptions,
): Promise<GenerateWriterAiDraftsData> {
  return kmiFetch<GenerateWriterAiDraftsData>('generate-writer-ai-drafts', {
    method: 'POST',
    signal: options.signal,
    body: options.input,
  });
}

export interface SaveWriterAiCalendarDraftOptions extends FetchSignal {
  readonly input: SaveWriterAiCalendarDraftInput;
}

/**
 * Task 292C3 — writer-scoped AI calendar save. Atomic across
 * content_items + content_variants + calendar_entries + one writing
 * task + one assignment (bound to the caller) per item, via the
 * `public.save_writer_ai_calendar_draft_v1` SECURITY DEFINER RPC.
 *
 * The written items land in `draft` / `content` discipline / `ai_assisted`
 * origin. The senior review path is the existing 292C2 lane; nothing
 * this endpoint does can send to the client, promote to production, or
 * schedule publishing.
 */
export function saveWriterAiCalendarDraft(
  options: SaveWriterAiCalendarDraftOptions,
): Promise<SaveWriterAiCalendarDraftData> {
  return kmiFetch<SaveWriterAiCalendarDraftData>('save-writer-ai-calendar-draft', {
    method: 'POST',
    signal: options.signal,
    body: options.input,
  });
}

// ── Task 068: calendar operations + review workflow ──────────────────

export interface ListCalendarEntriesByMonthParams extends FetchSignal {
  readonly clientId: string;
  /** `YYYY-MM`. Interpreted in the supplied timezone. */
  readonly month: string;
  /** IANA timezone string. Defaults to `UTC` server-side. */
  readonly timezone?: string;
}

/**
 * Fetch active `calendar_entries` for one client in one calendar
 * month, joined to their parent `content_variants` and `content_items`.
 * RLS gates visibility; the endpoint pre-flights `clients` to collapse
 * cross-org probes to 404.
 */
export function listCalendarEntriesByMonth(
  params: ListCalendarEntriesByMonthParams,
): Promise<ListCalendarEntriesByMonthData> {
  return kmiFetch<ListCalendarEntriesByMonthData>('list-calendar-entries-by-month', {
    method: 'GET',
    signal: params.signal,
    query: {
      client_id: params.clientId,
      month: params.month,
      timezone: params.timezone,
    },
  });
}

export interface UpdateContentCalendarDraftOptions extends FetchSignal {
  readonly input: UpdateContentCalendarDraftInput;
}

/**
 * Atomically edit one (content_item, content_variant, calendar_entry)
 * triple for a row currently in `status='draft'`. Calls the
 * `public.update_content_calendar_draft_v1` RPC. Review-state rows
 * must be returned to draft via {@link transitionContentItemStatus}
 * first; this endpoint will reject with 23514 on the wire as 400.
 *
 * Caller must hold `content.create` AND `content.variant.manage` AND
 * `content.schedule`.
 */
export function updateContentCalendarDraft(
  options: UpdateContentCalendarDraftOptions,
): Promise<UpdateContentCalendarDraftData> {
  return kmiFetch<UpdateContentCalendarDraftData>('update-content-calendar-draft', {
    method: 'POST',
    signal: options.signal,
    body: options.input,
  });
}

export interface TransitionContentItemStatusOptions extends FetchSignal {
  readonly input: TransitionContentItemStatusInput;
}

/**
 * Move a content item through the V1 review workflow:
 *
 *   draft → pending_internal_review (agency)
 *   pending_internal_review → internal_approved (records approval)
 *   pending_internal_review → draft (return-to-draft)
 *   internal_approved → pending_client_asset_approval     (Task 104B —
 *                                                          MOVED from
 *                                                          pending_client_content_approval;
 *                                                          step 5 of source-of-truth)
 *   internal_approved → draft (leadership-only)
 *   pending_client_content_approval → client_content_approved (records approval)
 *   pending_client_content_approval → draft
 *   pending_client_asset_approval → client_asset_approved   (Task 104B —
 *                                                            client final
 *                                                            asset approval)
 *   pending_client_asset_approval → draft (Task 104B — request changes)
 *   client_content_approved → draft (leadership-only)
 *   client_content_approved → in_production    (Task 101B — atomic
 *                                                production-task creation;
 *                                                idempotent on retry)
 *   client_asset_approved → draft (Task 104B — leadership-only revert)
 *
 * Permission gates differ per edge — see the RPC source. NO publishing,
 * NO platform execution.
 *
 * Task 101B — the response also carries `task_id` (uuid of the
 * production task on a handoff edge; null otherwise) and
 * `task_already_existed` (true on idempotent retry; false on a fresh
 * insert; false on every non-handoff edge).
 *
 * Task 104B — adds 3 new edges (above) + 3 new notification kinds
 * fired by the RPC (`client_asset_handoff`, `client_asset_approved_notice`,
 * `client_asset_request_changes`). The pre-104B
 * `internal_approved → pending_client_content_approval` edge is REMOVED
 * (it was the misroute that hid step 5). FE callers that hard-coded
 * the old target on the `internal_approved` button must switch to
 * `pending_client_asset_approval`.
 */
export function transitionContentItemStatus(
  options: TransitionContentItemStatusOptions,
): Promise<TransitionContentItemStatusData> {
  return kmiFetch<TransitionContentItemStatusData>('transition-content-item-status', {
    method: 'POST',
    signal: options.signal,
    body: options.input,
  });
}

// ── Task 101D: production deliverable upload ─────────────────────────
//
// Three thin wrappers over the new edge functions. ALL production-task
// state changes route through here. The FE never calls
// `from('tasks').update(...)` directly; the SECURITY DEFINER RPCs
// behind these endpoints are the single source of truth for the
// permission gate (agency role + active assignee + parent
// content_item.status='in_production' + production.asset.submit).
//
// NO publish, NO AI, NO content_items.status writes, NO
// creative-intelligence reuse from this surface.

export interface PrepareProductionTaskUploadOptions extends FetchSignal {
  readonly input: PrepareProductionTaskUploadInput;
}

/**
 * Mint a short-lived signed PUT URL for the `kmi-task-files` bucket.
 * The browser PUTs the file directly to that URL; the durable
 * `task_files` row is created later in `submitProductionTask`.
 *
 * The returned `storage_key` is the canonical tenant-scoped key
 * (`<org>/<client>/<task>/<file_uuid>/<filename>`); the FE MUST pass
 * the same key into `submitProductionTask` so the (task_id,
 * storage_key) idempotency holds.
 */
export function prepareProductionTaskUpload(
  options: PrepareProductionTaskUploadOptions,
): Promise<PrepareProductionTaskUploadData> {
  return kmiFetch<PrepareProductionTaskUploadData>('prepare-production-task-upload', {
    method: 'POST',
    signal: options.signal,
    body: options.input,
  });
}

export interface StartProductionTaskOptions extends FetchSignal {
  readonly input: StartProductionTaskInput;
}

/**
 * Transition a production task from 'open' or 'blocked' to
 * 'in_progress'. Re-call from 'in_progress' is an idempotent no-op
 * success (`task_already_started=true`). Any other status raises.
 */
export function startProductionTask(
  options: StartProductionTaskOptions,
): Promise<StartProductionTaskData> {
  return kmiFetch<StartProductionTaskData>('start-production-task', {
    method: 'POST',
    signal: options.signal,
    body: options.input,
  });
}

export interface SubmitProductionTaskOptions extends FetchSignal {
  readonly input: SubmitProductionTaskInput;
}

/**
 * Atomically attach the previously-uploaded deliverable file to the
 * task and flip the task to `'submitted'`. Idempotency is keyed on
 * `(task_id, storage_key)` so a duplicate submit returns the existing
 * `task_file_id` + `task_submission_id` with `task_already_submitted=true`.
 *
 * DOES NOT touch `content_items.status` — the post-`in_production`
 * content-item state machine is out of 101D scope.
 */
export function submitProductionTask(
  options: SubmitProductionTaskOptions,
): Promise<SubmitProductionTaskData> {
  return kmiFetch<SubmitProductionTaskData>('submit-production-task', {
    method: 'POST',
    signal: options.signal,
    body: options.input,
  });
}

export interface BlockProductionTaskOptions extends FetchSignal {
  readonly input: BlockProductionTaskInput;
}

/**
 * Task 215 — flag a production task `blocked` with a required reason
 * (assignee-gated SD RPC). `open`/`in_progress` → `blocked`; idempotent on an
 * already-blocked task. Notifies the client's managers (AM + MM; owner
 * fallback). Gates live in the RPC.
 */
export function blockProductionTask(
  options: BlockProductionTaskOptions,
): Promise<BlockProductionTaskData> {
  return kmiFetch<BlockProductionTaskData>('block-production-task', {
    method: 'POST',
    signal: options.signal,
    body: options.input,
  });
}

export interface RequestProductionTaskClarificationOptions extends FetchSignal {
  readonly input: RequestProductionTaskClarificationInput;
}

/**
 * Task 215 — send a clarification question to the client's managers
 * (assignee-gated SD RPC). Communication only — the task status is NOT changed.
 */
export function requestProductionTaskClarification(
  options: RequestProductionTaskClarificationOptions,
): Promise<RequestProductionTaskClarificationData> {
  return kmiFetch<RequestProductionTaskClarificationData>('request-production-task-clarification', {
    method: 'POST',
    signal: options.signal,
    body: options.input,
  });
}

// ── Task 220 — content-writer drafts + senior review (UI: Task 219) ──

export interface ListContentWriterDraftsParams extends FetchSignal {
  readonly status?: ContentWriterDraftStatus;
}
/** Reader for the caller's VISIBLE content-writer drafts (RLS-scoped: a writer
 *  sees their own; a senior sees the in-scope review queue via `?status=`). */
export function listContentWriterDrafts(
  params: ListContentWriterDraftsParams = {},
): Promise<ListContentWriterDraftsData> {
  return kmiFetch<ListContentWriterDraftsData>('list-content-writer-drafts', {
    method: 'GET',
    signal: params.signal,
    ...(params.status ? { query: { status: params.status } } : {}),
  });
}

export interface SaveContentWriterDraftOptions extends FetchSignal {
  readonly input: SaveContentWriterDraftInput;
}
/** Writer upserts their assignee-scoped text draft for a content variant. */
export function saveContentWriterDraft(
  options: SaveContentWriterDraftOptions,
): Promise<SaveContentWriterDraftData> {
  return kmiFetch<SaveContentWriterDraftData>('save-content-writer-draft', {
    method: 'POST',
    signal: options.signal,
    body: options.input,
  });
}

export interface SubmitContentWriterDraftOptions extends FetchSignal {
  readonly input: SubmitContentWriterDraftInput;
}
/** Writer submits their draft to the senior content writer for review. */
export function submitContentWriterDraft(
  options: SubmitContentWriterDraftOptions,
): Promise<SubmitContentWriterDraftData> {
  return kmiFetch<SubmitContentWriterDraftData>('submit-content-writer-draft', {
    method: 'POST',
    signal: options.signal,
    body: options.input,
  });
}

// ── Task 292C1 — the pre-client writing lane ────────────────────────

export interface ListEligibleContentWritersParams extends FetchSignal {
  readonly contentItemId: string;
}
/**
 * Writers the caller may hand this content item to.
 *
 * Gated server-side by the SAME predicate as the handoff itself
 * (`app.require_writing_handoff_authz`), so this can never list a writer the
 * handoff would then reject, and never leaks a roster to a caller who could
 * not act on it. An empty `writers` array is a valid answer.
 */
export function listEligibleContentWriters(
  params: ListEligibleContentWritersParams,
): Promise<ListEligibleContentWritersData> {
  const search = new URLSearchParams({ content_item_id: params.contentItemId }).toString();
  return kmiFetch<ListEligibleContentWritersData>(`list-eligible-content-writers?${search}`, {
    method: 'GET',
    signal: params.signal,
  });
}

export interface SendContentItemToWritingOptions extends FetchSignal {
  readonly input: SendContentItemToWritingInput;
}
/**
 * Hand a pre-client content item to a writer: creates OR reopens the ONE
 * writing task for the item and binds the chosen writer.
 *
 * Idempotent server-side (the RPC locks the content_item row), so a repeated
 * call returns the same task with `assignment_already_existed: true` rather
 * than creating a duplicate.
 */
export function sendContentItemToWriting(
  options: SendContentItemToWritingOptions,
): Promise<SendContentItemToWritingData> {
  return kmiFetch<SendContentItemToWritingData>('send-content-item-to-writing', {
    method: 'POST',
    signal: options.signal,
    body: options.input,
  });
}

export interface ReviewContentWriterDraftOptions extends FetchSignal {
  readonly input: ReviewContentWriterDraftInput;
}
/** Senior reviews a submitted draft: return / approve_internal /
 *  approve_and_send_to_client (the last reuses the content-workflow transition). */
export function reviewContentWriterDraft(
  options: ReviewContentWriterDraftOptions,
): Promise<ReviewContentWriterDraftData> {
  return kmiFetch<ReviewContentWriterDraftData>('review-content-writer-draft', {
    method: 'POST',
    signal: options.signal,
    body: options.input,
  });
}

export interface ListProductionTasksForContentItemParams extends FetchSignal {
  readonly contentItemId: string;
}

/**
 * Read-only lookup of the (at most one) non-cancelled production
 * task linked to a content_item, plus its active human assignment +
 * latest submission timestamp. Powers the inline production-task
 * panel inside ContentPage when the displayed item is `in_production`.
 *
 * RLS-bound. Callers without read access to the underlying tasks
 * row get an empty `tasks` array (same shape as
 * `list-content-creative-assets`).
 */
export function listProductionTasksForContentItem(
  params: ListProductionTasksForContentItemParams,
): Promise<ListProductionTasksForContentItemData> {
  const search = new URLSearchParams({ content_item_id: params.contentItemId }).toString();
  return kmiFetch<ListProductionTasksForContentItemData>(
    `list-production-tasks-for-content-item?${search}`,
    {
      method: 'GET',
      signal: params.signal,
    },
  );
}

// ── Task 117 / TEAM-2: production routing ─────────────────────────────

export interface AssignProductionTaskOptions extends FetchSignal {
  readonly input: AssignProductionTaskInput;
}

/**
 * Manually assign or reassign a production task to a member of the
 * Client Agency Team.
 *
 * Caller gate (LOCKED, Task 111 §5.0 D4 — enforced server-side by
 * `assign_production_task_v1`):
 *   - `owner` unconditional
 *   - OR `marketing_manager` unconditional
 *   - OR (`production_manager` AND active `client_assignment` for the
 *      task's parent client)
 *
 * No `task.assign`-based path. Wire errors the FE should map:
 *   - 409 `conflict` with `detail: 'task_in_terminal_state'` → the task
 *     is `submitted` / `completed` / `cancelled` (§5.0 D8). Show the
 *     "reassign forbidden after terminal" copy.
 *   - 403 `permission_denied` → caller is not in the locked set.
 *   - 404 `not_found` → cross-tenant / non-visible task or target.
 *   - 400 `validation_error` → target not on CAT, missing perms, etc.
 *
 * Backend not deployed yet — this endpoint is wired for PR-2; the EF
 * activation is a separate user-approved step.
 */
export function assignProductionTask(
  options: AssignProductionTaskOptions,
): Promise<AssignProductionTaskData> {
  return kmiFetch<AssignProductionTaskData>('assign-production-task', {
    method: 'POST',
    signal: options.signal,
    body: options.input,
  });
}

export interface ListEligibleProductionAssigneesParams extends FetchSignal {
  readonly taskId: string;
}

/**
 * Returns the augmented Client Agency Team roster for the task's
 * parent client (per-member profile + roles + disciplines +
 * `is_eligible_for_this_task`) plus the active assignee and parent
 * discipline. Powers the production-panel picker / empty state.
 *
 * Server-side via `list_eligible_production_assignees_v1` SECURITY
 * DEFINER RPC. Enforces the IDENTICAL caller gate as the assign RPC
 * (owner / marketing_manager / production_manager-with-CAT only).
 * Non-locked callers get 403 `permission_denied`.
 *
 * Backend not deployed yet — see `assignProductionTask` note.
 */
export function listEligibleProductionAssignees(
  params: ListEligibleProductionAssigneesParams,
): Promise<ListEligibleProductionAssigneesData> {
  const search = new URLSearchParams({ task_id: params.taskId }).toString();
  return kmiFetch<ListEligibleProductionAssigneesData>(
    `list-eligible-production-assignees?${search}`,
    {
      method: 'GET',
      signal: params.signal,
    },
  );
}

// ── Task 118 — Employee Workspace v1: list my production tasks ──────

export interface ListMyProductionTasksParams extends FetchSignal {}

/**
 * Read-only fetch of the caller's active production task assignments
 * for the Employee Workspace "مهامي" page (Task 118).
 *
 * RLS-bound. Returns ONLY rows where:
 *   - `task_assignments.assignee_user_id = auth.uid()` (admitted by
 *     the existing `task_assignments_select` assignee-match branch).
 *   - `task_assignments.ended_at IS NULL` (active only).
 *   - `tasks.source_type = 'content_item'` (production tasks).
 *   - `tasks.status IN ('open','in_progress','submitted','blocked')`
 *     (terminal `completed` / `cancelled` excluded).
 *
 * Sysadmin → 404 via `buildAiActorContext`. Empty list is the normal
 * shape when the caller has no active assignments — no error.
 */
export function listMyProductionTasks(
  params: ListMyProductionTasksParams = {},
): Promise<ListMyProductionTasksData> {
  return kmiFetch<ListMyProductionTasksData>('list-my-production-tasks', {
    method: 'GET',
    signal: params.signal,
  });
}

// ── Task 069 + Task 104B: client approval inbox ──────────────────────

export interface ListPendingClientApprovalsParams extends FetchSignal {
  readonly clientId: string;
  /**
   * Task 104B — which client-side approval queue to fetch.
   *   `'content'` (default; pre-104B behavior preserved): items at
   *               `pending_client_content_approval` (pre-production
   *               approval of the draft text/script/visual direction).
   *   `'asset'`: items at `pending_client_asset_approval` (final
   *              produced-asset approval — the deliverable submitted
   *              via `submitProductionTask` and approved internally
   *              by the agency).
   * Shape of the response is identical for both stages.
   */
  readonly stage?: PendingClientApprovalStage;
}

/**
 * Fetch content_items pending a client decision, joined to their
 * `content_variants` and `calendar_entries` for caption +
 * scheduled-time previews. Used by the client-side approval inbox.
 *
 * Stage selection (Task 104B):
 *   - `stage='content'` (default; preserves pre-104B behavior) → items at
 *     `pending_client_content_approval`.
 *   - `stage='asset'` → items at `pending_client_asset_approval`.
 *
 * RLS-bound. Sysadmin → 404. Agency users CAN call this and read the
 * same data; the security boundary is not the read but the action —
 * `transitionContentItemStatus` enforces client-category role for
 * the actual approval transition (both `client_content_approved` and
 * the new `client_asset_approved`).
 *
 * The wire response includes the resolved `stage` field so the FE can
 * label the queue without re-parsing the URL it sent.
 */
export function listPendingClientApprovals(
  params: ListPendingClientApprovalsParams,
): Promise<ListPendingClientApprovalsData> {
  const query: Record<string, string> = { client_id: params.clientId };
  if (params.stage !== undefined) {
    query['stage'] = params.stage;
  }
  return kmiFetch<ListPendingClientApprovalsData>('list-pending-client-approvals', {
    method: 'GET',
    signal: params.signal,
    query,
  });
}

// ── paid media ───────────────────────────────────────────────────────

export interface ListAdAccountsParams extends FetchSignal {
  readonly clientId?: string;
  readonly platform?: PaidMediaPlatform;
  readonly limit?: number;
  readonly cursor?: string;
}

export function listAdAccounts(params: ListAdAccountsParams = {}): Promise<ListAdAccountsData> {
  return kmiFetch<ListAdAccountsData>('list-ad-accounts', {
    method: 'GET',
    signal: params.signal,
    query: {
      client_id: params.clientId,
      platform: params.platform,
      limit: params.limit === undefined ? undefined : String(params.limit),
      cursor: params.cursor,
    },
  });
}

export interface ListPaidMediaCampaignsParams extends FetchSignal {
  readonly clientId?: string;
  readonly adAccountId?: string;
  readonly platform?: PaidMediaPlatform;
  readonly status?: PaidMediaEntityStatus;
  readonly limit?: number;
  readonly cursor?: string;
}

export function listPaidMediaCampaigns(
  params: ListPaidMediaCampaignsParams = {},
): Promise<ListPaidMediaCampaignsData> {
  return kmiFetch<ListPaidMediaCampaignsData>('list-paid-media-campaigns', {
    method: 'GET',
    signal: params.signal,
    query: {
      client_id: params.clientId,
      ad_account_id: params.adAccountId,
      platform: params.platform,
      status: params.status,
      limit: params.limit === undefined ? undefined : String(params.limit),
      cursor: params.cursor,
    },
  });
}

/**
 * Task 148 (IC-5 MVP) — client-facing aggregate read.
 *
 * Targets the new `list-client-paid-campaigns` EF, which runs under
 * service-role but gates on the caller's client-category role +
 * client_assignments + tenant boundary. Use this from the CLIENT
 * paid-media page (PaidMediaPage.tsx); the AGENCY page continues to
 * call `listPaidMediaCampaigns` (which goes through agency-RLS).
 *
 * No `platform` param — the EF hard-locks to `platform='meta_ads'`
 * (Meta is the only provider IC-5 ships).
 */
export interface ListClientPaidCampaignsParams extends FetchSignal {
  readonly clientId: string;
  readonly adAccountId?: string;
  readonly status?: PaidMediaEntityStatus;
  readonly limit?: number;
  readonly cursor?: string;
}

export function listClientPaidCampaigns(
  params: ListClientPaidCampaignsParams,
): Promise<ListClientPaidCampaignsData> {
  return kmiFetch<ListClientPaidCampaignsData>('list-client-paid-campaigns', {
    method: 'GET',
    signal: params.signal,
    query: {
      client_id: params.clientId,
      ad_account_id: params.adAccountId,
      status: params.status,
      limit: params.limit === undefined ? undefined : String(params.limit),
      cursor: params.cursor,
    },
  });
}

/**
 * Task 148 (IC-5 MVP) — manual trigger for the paid-media campaign
 * ingestion adapter. Agency-leadership (or paid_media.account.manage
 * grant) only; the EF returns the sync-run summary.
 *
 * Task 155 (IC-5.3): the EF now forwards a per-entity breakdown on top of
 * the base summary, so the response is typed as the richer (web-local)
 * `PaidMediaSyncSummaryData`. The per-entity fields are optional, so an
 * older EF/Worker response (base 4 fields only) still satisfies the type.
 */
export function syncPaidMediaMetaCampaigns(
  input: SyncPaidMediaMetaCampaignsInput,
  opts: FetchSignal = {},
): Promise<PaidMediaSyncSummaryData> {
  return kmiFetch<PaidMediaSyncSummaryData>('sync-paid-media-meta-campaigns', {
    method: 'POST',
    signal: opts.signal,
    body: input,
  });
}

/**
 * IC-7.4 (Task 195) — manual Snapchat campaign-level sync trigger. Dedicated
 * twin of `syncPaidMediaMetaCampaigns` that POSTs to the
 * `sync-paid-media-snapchat-campaigns` EF. Authenticated FE flow (the user's
 * JWT, NEVER service-role). The response is typed as the richer (web-local)
 * `SnapchatPaidMediaSyncSummaryData` (ad_squads, no creatives); the per-entity
 * fields are optional so a base-4-fields response still satisfies the type.
 */
export function syncPaidMediaSnapchatCampaigns(
  input: { readonly ad_account_id: string },
  opts: FetchSignal = {},
): Promise<SnapchatPaidMediaSyncSummaryData> {
  return kmiFetch<SnapchatPaidMediaSyncSummaryData>('sync-paid-media-snapchat-campaigns', {
    method: 'POST',
    signal: opts.signal,
    body: input,
  });
}

export function getPaidMediaCampaign(
  campaignId: string,
  opts: FetchSignal = {},
): Promise<GetPaidMediaCampaignData> {
  return kmiFetch<GetPaidMediaCampaignData>('get-paid-media-campaign', {
    method: 'GET',
    signal: opts.signal,
    query: { campaign_id: campaignId },
  });
}

export interface ListPaidMediaAdSetsParams extends FetchSignal {
  readonly limit?: number;
  readonly cursor?: string;
}

export function listPaidMediaAdSets(
  paidCampaignId: string,
  opts: ListPaidMediaAdSetsParams = {},
): Promise<ListPaidMediaAdSetsData> {
  return kmiFetch<ListPaidMediaAdSetsData>('list-paid-media-ad-sets', {
    method: 'GET',
    signal: opts.signal,
    query: {
      paid_campaign_id: paidCampaignId,
      limit: opts.limit === undefined ? undefined : String(opts.limit),
      cursor: opts.cursor,
    },
  });
}

export interface ListPaidMediaAdsParams extends FetchSignal {
  readonly limit?: number;
  readonly cursor?: string;
}

export function listPaidMediaAds(
  adSetId: string,
  opts: ListPaidMediaAdsParams = {},
): Promise<ListPaidMediaAdsData> {
  return kmiFetch<ListPaidMediaAdsData>('list-paid-media-ads', {
    method: 'GET',
    signal: opts.signal,
    query: {
      ad_set_id: adSetId,
      limit: opts.limit === undefined ? undefined : String(opts.limit),
      cursor: opts.cursor,
    },
  });
}

export interface ListPaidMediaMetricsParams extends FetchSignal {
  readonly paidCampaignId: string;
  readonly windowStart: string;
  readonly windowEnd: string;
  readonly metricLevel?: PaidMediaMetricLevel;
  readonly adSetId?: string;
  readonly adId?: string;
  readonly limit?: number;
  readonly cursor?: string;
}

export function listPaidMediaMetrics(
  params: ListPaidMediaMetricsParams,
): Promise<ListPaidMediaMetricsData> {
  return kmiFetch<ListPaidMediaMetricsData>('list-paid-media-metrics', {
    method: 'GET',
    signal: params.signal,
    query: {
      paid_campaign_id: params.paidCampaignId,
      window_start: params.windowStart,
      window_end: params.windowEnd,
      metric_level: params.metricLevel,
      ad_set_id: params.adSetId,
      ad_id: params.adId,
      limit: params.limit === undefined ? undefined : String(params.limit),
      cursor: params.cursor,
    },
  });
}

export interface GetPaidMediaPerformanceSummaryParams extends FetchSignal {
  readonly paidCampaignId: string;
  readonly windowStart: string;
  readonly windowEnd: string;
}

/**
 * Task 157 — aggregated performance summary (KPIs + daily series + per-level
 * breakdown) for one campaign + window. Powers the analytics cards/chart; the
 * raw daily table keeps using `listPaidMediaMetrics`.
 */
export function getPaidMediaPerformanceSummary(
  params: GetPaidMediaPerformanceSummaryParams,
): Promise<PaidMediaPerformanceSummaryData> {
  return kmiFetch<PaidMediaPerformanceSummaryData>('get-paid-media-performance-summary', {
    method: 'GET',
    signal: params.signal,
    query: {
      paid_campaign_id: params.paidCampaignId,
      window_start: params.windowStart,
      window_end: params.windowEnd,
    },
  });
}

// ── creative intelligence ────────────────────────────────────────────

export interface ListCreativeIntelligenceContentGroupsParams extends FetchSignal {
  readonly clientId: string;
  readonly dateFrom: string; // YYYY-MM-DD
  readonly dateTo: string; // YYYY-MM-DD
  readonly campaignId?: string;
}

/**
 * Task 221 — read-only cross-platform content groups for one client + window.
 * Groups the client's paid ads across Meta / Snapchat / TikTok by normalized
 * name and returns per-platform slices + deterministic signals. Read-only.
 */
export function listCreativeIntelligenceContentGroups(
  params: ListCreativeIntelligenceContentGroupsParams,
): Promise<ListCreativeIntelligenceContentGroupsData> {
  return kmiFetch<ListCreativeIntelligenceContentGroupsData>(
    'list-creative-intelligence-content-groups',
    {
      method: 'GET',
      signal: params.signal,
      query: {
        client_id: params.clientId,
        date_from: params.dateFrom,
        date_to: params.dateTo,
        campaign_id: params.campaignId,
      },
    },
  );
}

export function listCreativeAssets(
  clientId: string | null,
  opts: FetchSignal = {},
): Promise<ListCreativeAssetsData> {
  return kmiFetch<ListCreativeAssetsData>('list-creative-assets', {
    method: 'GET',
    signal: opts.signal,
    query: clientId ? { client_id: clientId } : {},
  });
}

export function getCreativeAsset(
  assetId: string,
  opts: FetchSignal = {},
): Promise<GetCreativeAssetData> {
  return kmiFetch<GetCreativeAssetData>('get-creative-asset', {
    method: 'GET',
    signal: opts.signal,
    query: { asset_id: assetId },
  });
}

export function listCreativeAssetMatches(
  assetId: string,
  opts: FetchSignal = {},
): Promise<ListCreativeAssetMatchesData> {
  return kmiFetch<ListCreativeAssetMatchesData>('list-creative-asset-matches', {
    method: 'GET',
    signal: opts.signal,
    query: { asset_id: assetId },
  });
}

/**
 * Task 222 B1 — read-only paid-media match suggestions for one creative asset +
 * client + date window. Returns existing links, scored suggestions (exact /
 * alias / fuzzy), unmatched ads, and platform connection state. No writes.
 */
export function suggestCreativeAssetPaidMatches(
  params: SuggestCreativeAssetPaidMatchesParams,
): Promise<SuggestCreativeAssetPaidMatchesData> {
  return kmiFetch<SuggestCreativeAssetPaidMatchesData>('suggest-creative-asset-paid-matches', {
    method: 'GET',
    signal: params.signal,
    query: {
      asset_id: params.assetId,
      client_id: params.clientId,
      date_from: params.dateFrom,
      date_to: params.dateTo,
    },
  });
}

/**
 * Task 222 B1 — manually link a creative asset to a paid-media ad. Thin
 * authenticated insert; same-org/client + actor provenance + no-dup-active are
 * enforced by the DB (RLS + trigger). Returns the created match row.
 */
export function createCreativeAssetMatch(
  input: CreateCreativeAssetMatchInput,
  opts: FetchSignal = {},
): Promise<CreateCreativeAssetMatchData> {
  return kmiFetch<CreateCreativeAssetMatchData>('create-creative-asset-match', {
    method: 'POST',
    signal: opts.signal,
    body: { asset_id: input.asset_id, ad_id: input.ad_id },
  });
}

// ── influencers ──────────────────────────────────────────────────────

export function listInfluencers(opts: FetchSignal = {}): Promise<ListInfluencersData> {
  return kmiFetch<ListInfluencersData>('list-influencers', { method: 'GET', signal: opts.signal });
}

export function getInfluencer(id: string, opts: FetchSignal = {}): Promise<GetInfluencerData> {
  return kmiFetch<GetInfluencerData>('get-influencer', {
    method: 'GET',
    signal: opts.signal,
    query: { id },
  });
}

export function listInfluencerPlatformAccounts(
  influencerId: string,
  opts: FetchSignal = {},
): Promise<ListInfluencerPlatformAccountsData> {
  return kmiFetch<ListInfluencerPlatformAccountsData>('list-influencer-platform-accounts', {
    method: 'GET',
    signal: opts.signal,
    query: { influencer_id: influencerId },
  });
}

// ── reports ──────────────────────────────────────────────────────────

export interface ListReportJobsParams extends FetchSignal {
  readonly clientId?: string;
  readonly status?: ReportJobStatus;
}

export function listReportJobs(params: ListReportJobsParams = {}): Promise<ListReportJobsData> {
  return kmiFetch<ListReportJobsData>('list-report-jobs', {
    method: 'GET',
    signal: params.signal,
    query: { client_id: params.clientId, status: params.status },
  });
}

export function getReportJob(id: string, opts: FetchSignal = {}): Promise<GetReportJobData> {
  return kmiFetch<GetReportJobData>('get-report-job', {
    method: 'GET',
    signal: opts.signal,
    query: { id },
  });
}

export function listReportArtifacts(
  reportJobId: string,
  opts: FetchSignal = {},
): Promise<ListReportArtifactsData> {
  return kmiFetch<ListReportArtifactsData>('list-report-artifacts', {
    method: 'GET',
    signal: opts.signal,
    query: { report_job_id: reportJobId },
  });
}

// ── crm ──────────────────────────────────────────────────────────────

export interface ListCrmLeadsParams extends FetchSignal {
  readonly status?: CrmLeadStatus;
  readonly ownerUserId?: string;
  readonly limit?: number;
}

export function listCrmLeads(params: ListCrmLeadsParams = {}): Promise<ListCrmLeadsData> {
  return kmiFetch<ListCrmLeadsData>('list-crm-leads', {
    method: 'GET',
    signal: params.signal,
    query: {
      status: params.status,
      owner_user_id: params.ownerUserId,
      limit: params.limit,
    },
  });
}

export function getCrmLead(id: string, opts: FetchSignal = {}): Promise<GetCrmLeadData> {
  return kmiFetch<GetCrmLeadData>('get-crm-lead', {
    method: 'GET',
    signal: opts.signal,
    query: { id },
  });
}

export interface ListCrmDealsParams extends FetchSignal {
  readonly stage?: CrmDealStage;
  readonly leadId?: string;
  readonly ownerUserId?: string;
  readonly limit?: number;
}

export function listCrmDeals(params: ListCrmDealsParams = {}): Promise<ListCrmDealsData> {
  return kmiFetch<ListCrmDealsData>('list-crm-deals', {
    method: 'GET',
    signal: params.signal,
    query: {
      stage: params.stage,
      lead_id: params.leadId,
      owner_user_id: params.ownerUserId,
      limit: params.limit,
    },
  });
}

export function getCrmDeal(id: string, opts: FetchSignal = {}): Promise<GetCrmDealData> {
  return kmiFetch<GetCrmDealData>('get-crm-deal', {
    method: 'GET',
    signal: opts.signal,
    query: { id },
  });
}

export function listCrmDealActivities(
  dealId: string,
  opts: FetchSignal = {},
): Promise<ListCrmDealActivitiesData> {
  return kmiFetch<ListCrmDealActivitiesData>('list-crm-deal-activities', {
    method: 'GET',
    signal: opts.signal,
    query: { deal_id: dealId },
  });
}

export function listCrmDealStageHistory(
  dealId: string,
  opts: FetchSignal = {},
): Promise<ListCrmDealStageHistoryData> {
  return kmiFetch<ListCrmDealStageHistoryData>('list-crm-deal-stage-history', {
    method: 'GET',
    signal: opts.signal,
    query: { deal_id: dealId },
  });
}

// ── publishing ───────────────────────────────────────────────────────

export function listSocialConnections(
  clientId: string,
  opts: FetchSignal = {},
): Promise<ListSocialConnectionsData> {
  return kmiFetch<ListSocialConnectionsData>('list-social-connections', {
    method: 'GET',
    signal: opts.signal,
    query: { client_id: clientId },
  });
}

// Task 112C — start social-connection OAuth flow.
//
// Returns the provider's authorize URL the FE opens (popup OR
// top-level navigation; FE chooses). The state token is the CSRF
// secret — the FE forwards it back to `complete-social-connection-oauth`
// alongside the provider-returned `code`. NEVER persist the state
// token in localStorage / sessionStorage — it lives only in
// memory for the lifetime of the OAuth dance (10 min TTL on the
// server-side row anyway).
export interface StartSocialConnectionOAuthOptions extends FetchSignal {
  readonly input: StartSocialConnectionOAuthInput;
}

export function startSocialConnectionOAuth(
  options: StartSocialConnectionOAuthOptions,
): Promise<StartSocialConnectionOAuthData> {
  return kmiFetch<StartSocialConnectionOAuthData>('start-social-connection-oauth', {
    method: 'POST',
    signal: options.signal,
    body: {
      client_id: options.input.client_id,
      platform: options.input.platform,
      redirect_back_to: options.input.redirect_back_to ?? null,
    },
  });
}

// Task 112C — disconnect a social connection. Soft-archive only;
// no provider-side OAuth-revoke call is fired (a follow-up task
// adds that).
export interface DisconnectSocialConnectionOptions extends FetchSignal {
  readonly input: DisconnectSocialConnectionInput;
}

export function disconnectSocialConnection(
  options: DisconnectSocialConnectionOptions,
): Promise<DisconnectSocialConnectionData> {
  return kmiFetch<DisconnectSocialConnectionData>('disconnect-social-connection', {
    method: 'POST',
    signal: options.signal,
    body: {
      connection_id: options.input.connection_id,
      reason: options.input.reason ?? null,
    },
  });
}

// ── publishing workflow V1 (Task 104C) ───────────────────────────────

export interface ListPublishJobsParams extends FetchSignal {
  readonly publishTargetId?: string;
  readonly contentItemId?: string;
  readonly clientId?: string;
  readonly status?: PublishJobStatus;
}

/**
 * Read publish_jobs visible to the caller. RLS on publish_jobs is the
 * load-bearing visibility gate (Task 014 §5.3). Task 104C extension:
 *   - `contentItemId` filters by parent content_item via the variant→
 *     target chain (the endpoint resolves target ids server-side).
 *   - `clientId` filters across every content_item for the given
 *     client (used by the per-client Publishing page).
 *   - The wire shape includes the embedded publish_target /
 *     content_variant / content_item triple plus the manual mark-
 *     published metadata (external_post_url, external_post_id,
 *     completed_by_user_id, etc).
 */
export function listPublishJobs(params: ListPublishJobsParams = {}): Promise<ListPublishJobsData> {
  return kmiFetch<ListPublishJobsData>('list-publish-jobs', {
    method: 'GET',
    signal: params.signal,
    query: {
      publish_target_id: params.publishTargetId,
      content_item_id: params.contentItemId,
      client_id: params.clientId,
      status: params.status,
    },
  });
}

export interface ScheduleContentItemPublishOptions extends FetchSignal {
  readonly input: ScheduleContentItemPublishInput;
}

/**
 * Schedule manual publish jobs for a content_item across one or more
 * V1 platforms (instagram / tiktok / snapchat). Wraps
 * `POST /functions/v1/schedule-content-item-publish`, which delegates
 * to `public.schedule_content_item_publish_v1` (see
 * `supabase/migrations/20260507150000_publishing_workflow_v1.sql`).
 *
 * Caller must hold `publish.schedule` AND be an agency user (leadership
 * or assigned to the parent client). The RPC re-checks both gates
 * server-side.
 */
export function scheduleContentItemPublish(
  options: ScheduleContentItemPublishOptions,
): Promise<ScheduleContentItemPublishData> {
  return kmiFetch<ScheduleContentItemPublishData>('schedule-content-item-publish', {
    method: 'POST',
    signal: options.signal,
    body: {
      content_item_id: options.input.content_item_id,
      scheduled_for: options.input.scheduled_for,
      platforms: options.input.platforms,
      // Caption overrides intentionally NOT sent — captions are
      // immutable after client_asset_approved (Task 104C round 2).
      // The edge function rejects any caption_overrides field on the
      // wire; this omission is the matching FE-side guard.
      notes: options.input.notes ?? null,
    },
  });
}

// 104D auto-publish endpoint wrapper DEFERRED — see types.ts comment
// "Task 104D — automated publishing FE wire surface DEFERRED" for
// rationale. Backend ships in this branch; FE wrapper lands in a
// follow-up task once the 105A `not.toMatch(/interface ScheduleContentItemAuto/)`
// pin is consciously revisited.

export interface MarkPublishJobPublishedOptions extends FetchSignal {
  readonly input: MarkPublishJobPublishedInput;
}

/**
 * Record a manual mark-published for a single publish_job. Inserts a
 * publish_attempts row (method=manual, status=succeeded) plus a
 * publish_log row, flips the job + variant, and finalizes the parent
 * content_item to status='published' if no other active jobs remain.
 */
export function markPublishJobPublished(
  options: MarkPublishJobPublishedOptions,
): Promise<MarkPublishJobPublishedData> {
  return kmiFetch<MarkPublishJobPublishedData>('mark-publish-job-published', {
    method: 'POST',
    signal: options.signal,
    body: {
      publish_job_id: options.input.publish_job_id,
      external_post_url: options.input.external_post_url ?? null,
      external_post_id: options.input.external_post_id ?? null,
      notes: options.input.notes ?? null,
    },
  });
}

export interface MarkPublishJobFailedOptions extends FetchSignal {
  readonly input: MarkPublishJobFailedInput;
}

/**
 * Record a manual mark-failed for a single publish_job. The parent
 * content_item stays at status='scheduled' so the user can retry or
 * mark published later.
 */
export function markPublishJobFailed(
  options: MarkPublishJobFailedOptions,
): Promise<MarkPublishJobFailedData> {
  return kmiFetch<MarkPublishJobFailedData>('mark-publish-job-failed', {
    method: 'POST',
    signal: options.signal,
    body: {
      publish_job_id: options.input.publish_job_id,
      reason: options.input.reason,
    },
  });
}

export interface CancelPublishJobOptions extends FetchSignal {
  readonly input: CancelPublishJobInput;
}

/**
 * Cancel a single publish_job. Archives the parent publish_target,
 * reverts the parent content_variant to status='approved', and (if
 * no other active jobs remain) reverts the parent content_item to
 * status='client_asset_approved' so a fresh schedule call can be
 * issued from the FE.
 */
export function cancelPublishJob(options: CancelPublishJobOptions): Promise<CancelPublishJobData> {
  return kmiFetch<CancelPublishJobData>('cancel-publish-job', {
    method: 'POST',
    signal: options.signal,
    body: {
      publish_job_id: options.input.publish_job_id,
      reason: options.input.reason ?? null,
    },
  });
}

// ── accept-invitation (anonymous mutation, kept for the public route) ─

export interface AcceptInvitationInput {
  readonly token: string;
  readonly password: string;
  readonly full_name?: string | null;
}

export function acceptInvitation(input: AcceptInvitationInput): Promise<AcceptInvitationData> {
  return kmiFetch<AcceptInvitationData>('accept-invitation', {
    method: 'POST',
    anonymous: true,
    body: {
      token: input.token,
      password: input.password,
      full_name: input.full_name ?? null,
    },
  });
}

// ── signup-agency (self-serve agency owner signup, Task agency-signup-v1) ─
//
// Anonymous mutations, public route. Two steps since Task 293B:
//
//   1. `signupAgencyInitiate` collects the tenant details and asks the
//      server to mail a one-time link. It creates nothing. Its response
//      is identical whether or not the address is already registered, so
//      the caller MUST NOT branch on it — there is nothing to branch on.
//   2. `signupAgencyVerify` presents the token from that link together
//      with the chosen password and completes the bootstrap exactly once.
//
// The password is supplied only at step 2 and is never sent, stored, or
// held anywhere at step 1. `full_name` / `agency_name` are NOT resent at
// step 2 — the server reads them from the pending record it created, so
// a stolen link cannot rename the tenant it creates.
//
// The EF MUST NOT mint a session; `AgencySignupPage` calls
// `supabase.auth.signInWithPassword` after a successful verify.
export interface SignupAgencyInitiateInput {
  readonly full_name: string;
  readonly email: string;
  readonly agency_name: string;
}

export interface SignupAgencyVerifyInput {
  readonly email: string;
  readonly token: string;
  readonly password: string;
}

export function signupAgencyInitiate(
  input: SignupAgencyInitiateInput,
): Promise<SignupAgencyInitiateData> {
  return kmiFetch<SignupAgencyInitiateData>('signup-agency', {
    method: 'POST',
    anonymous: true,
    body: {
      step: 'initiate',
      full_name: input.full_name,
      email: input.email,
      agency_name: input.agency_name,
    },
  });
}

export function signupAgencyVerify(input: SignupAgencyVerifyInput): Promise<SignupAgencyData> {
  return kmiFetch<SignupAgencyData>('signup-agency', {
    method: 'POST',
    anonymous: true,
    body: {
      step: 'verify',
      email: input.email,
      token: input.token,
      password: input.password,
    },
  });
}

// ── agency services / packages / assignments (Task 046) ──────────────

// ── team / invitations ─────────────────────────────────────────────────────

export interface ListRolesParams extends FetchSignal {
  readonly category?: Extract<RoleCategory, 'agency' | 'client'>;
}

export function listRoles(params: ListRolesParams = {}): Promise<ListRolesData> {
  return kmiFetch<ListRolesData>('list-roles', {
    method: 'GET',
    signal: params.signal,
    query: { category: params.category },
  });
}

export interface ListUserInvitationsParams extends FetchSignal {
  readonly clientId?: string;
  readonly status?: 'pending' | 'accepted' | 'expired' | 'revoked';
}

export function listUserInvitations(
  params: ListUserInvitationsParams = {},
): Promise<ListUserInvitationsData> {
  return kmiFetch<ListUserInvitationsData>('list-user-invitations', {
    method: 'GET',
    signal: params.signal,
    query: { client_id: params.clientId, status: params.status },
  });
}

export interface ListUserRolesParams extends FetchSignal {
  readonly clientId?: string;
}

export function listUserRoles(params: ListUserRolesParams = {}): Promise<ListUserRolesData> {
  return kmiFetch<ListUserRolesData>('list-user-roles', {
    method: 'GET',
    signal: params.signal,
    query: { client_id: params.clientId },
  });
}

export interface SendUserInvitationOptions extends FetchSignal {
  readonly input: SendUserInvitationInput;
}

export function sendUserInvitation(
  options: SendUserInvitationOptions,
): Promise<SendUserInvitationData> {
  return kmiFetch<SendUserInvitationData>('send-user-invitation', {
    method: 'POST',
    signal: options.signal,
    body: {
      email: options.input.email,
      role_id: options.input.role_id,
      client_id: options.input.client_id ?? null,
    },
  });
}

export interface RevokeUserInvitationOptions extends FetchSignal {
  readonly input: RevokeUserInvitationInput;
}

export function revokeUserInvitation(
  options: RevokeUserInvitationOptions,
): Promise<RevokeUserInvitationData> {
  return kmiFetch<RevokeUserInvitationData>('revoke-user-invitation', {
    method: 'POST',
    signal: options.signal,
    body: { invitation_id: options.input.invitation_id },
  });
}

export interface ListAgencyServicesParams extends FetchSignal {
  readonly active?: boolean;
  readonly category?: string;
}

export function listAgencyServices(
  params: ListAgencyServicesParams = {},
): Promise<ListAgencyServicesData> {
  return kmiFetch<ListAgencyServicesData>('list-agency-services', {
    method: 'GET',
    signal: params.signal,
    query: {
      active: params.active === undefined ? undefined : params.active ? 'true' : 'false',
      category: params.category,
    },
  });
}

export function bootstrapAgencyServices(
  opts: FetchSignal = {},
): Promise<BootstrapAgencyServicesData> {
  return kmiFetch<BootstrapAgencyServicesData>('bootstrap-agency-services', {
    method: 'POST',
    signal: opts.signal,
    body: {},
  });
}

export interface UpsertAgencyServiceOptions extends FetchSignal {
  readonly input: UpsertAgencyServiceInput;
}

export function upsertAgencyService(
  options: UpsertAgencyServiceOptions,
): Promise<UpsertAgencyServiceData> {
  // Forward only the keys the caller explicitly provided so the runtime
  // PATCH semantic (absent = preserve) is preserved.
  const body: Record<string, unknown> = {};
  for (const k of [
    'id',
    'name',
    'description',
    'category',
    'system_capability_key',
    'capability_keys',
    'default_price_amount',
    'default_price_currency',
    'billing_interval',
    'is_active',
    'metadata',
  ] as const) {
    if (k in options.input) body[k] = options.input[k] as unknown;
  }
  return kmiFetch<UpsertAgencyServiceData>('upsert-agency-service', {
    method: 'POST',
    signal: options.signal,
    body,
  });
}

export interface ListAgencyPackagesParams extends FetchSignal {
  readonly active?: boolean;
}

export function listAgencyPackages(
  params: ListAgencyPackagesParams = {},
): Promise<ListAgencyPackagesData> {
  return kmiFetch<ListAgencyPackagesData>('list-agency-packages', {
    method: 'GET',
    signal: params.signal,
    query: {
      active: params.active === undefined ? undefined : params.active ? 'true' : 'false',
    },
  });
}

export interface UpsertAgencyPackageOptions extends FetchSignal {
  readonly input: UpsertAgencyPackageInput;
}

export function upsertAgencyPackage(
  options: UpsertAgencyPackageOptions,
): Promise<UpsertAgencyPackageData> {
  const body: Record<string, unknown> = {};
  for (const k of [
    'id',
    'name',
    'description',
    'price_amount',
    'price_currency',
    'billing_interval',
    'is_active',
    'metadata',
  ] as const) {
    if (k in options.input) body[k] = options.input[k] as unknown;
  }
  return kmiFetch<UpsertAgencyPackageData>('upsert-agency-package', {
    method: 'POST',
    signal: options.signal,
    body,
  });
}

export interface SetAgencyPackageServicesOptions extends FetchSignal {
  readonly input: SetAgencyPackageServicesInput;
}

export function setAgencyPackageServices(
  options: SetAgencyPackageServicesOptions,
): Promise<SetAgencyPackageServicesData> {
  return kmiFetch<SetAgencyPackageServicesData>('set-agency-package-services', {
    method: 'POST',
    signal: options.signal,
    body: {
      package_id: options.input.package_id,
      services: options.input.services.map((s) => ({
        service_id: s.service_id,
        quantity: s.quantity ?? null,
        notes: s.notes ?? null,
        deliverables: s.deliverables ?? [],
        pricing_model: s.pricing_model ?? null,
        retainer_amount: s.retainer_amount ?? null,
        retainer_currency: s.retainer_currency,
        spend_percentage: s.spend_percentage ?? null,
        cadence: s.cadence ?? null,
        included: s.included,
        sort_order: s.sort_order,
        terms_notes: s.terms_notes ?? null,
        // Task 151-B: forward the per-service-type config object so the
        // package builder's per-line settings actually reach the EF.
        // (151-A added `config` to the wire/types but this client map
        // dropped it.)
        config: s.config ?? {},
      })),
    },
  });
}

export interface ListClientPackageAssignmentsParams extends FetchSignal {
  readonly clientId?: string;
}

export function listClientPackageAssignments(
  params: ListClientPackageAssignmentsParams = {},
): Promise<ListClientPackageAssignmentsData> {
  return kmiFetch<ListClientPackageAssignmentsData>('list-client-package-assignments', {
    method: 'GET',
    signal: params.signal,
    query: { client_id: params.clientId },
  });
}

export interface AssignClientPackageOptions extends FetchSignal {
  readonly input: AssignClientPackageInput;
}

export function assignClientPackage(
  options: AssignClientPackageOptions,
): Promise<AssignClientPackageData> {
  const body: Record<string, unknown> = {
    client_id: options.input.client_id,
    package_id: options.input.package_id,
  };
  for (const k of [
    'status',
    'starts_at',
    'ends_at',
    'price_amount_snapshot',
    'price_currency_snapshot',
    'billing_interval_snapshot',
    'notes',
  ] as const) {
    if (k in options.input) body[k] = options.input[k] as unknown;
  }
  return kmiFetch<AssignClientPackageData>('assign-client-package', {
    method: 'POST',
    signal: options.signal,
    body,
  });
}

export interface UpdateClientPackageAssignmentOptions extends FetchSignal {
  readonly input: UpdateClientPackageAssignmentInput;
}

// Task 151-C — explicit status transition (activate / end / cancel) for
// an existing client package assignment. `activate` ends the current
// active assignment and activates the target atomically (server-side
// RPC); `end`/`cancel` are single-row transitions.
export function updateClientPackageAssignment(
  options: UpdateClientPackageAssignmentOptions,
): Promise<UpdateClientPackageAssignmentData> {
  return kmiFetch<UpdateClientPackageAssignmentData>('update-client-package-assignment', {
    method: 'POST',
    signal: options.signal,
    body: {
      assignment_id: options.input.assignment_id,
      action: options.input.action,
    },
  });
}

// Task 151-D — monthly entitlement progress + carry-over.

export interface GetClientPackageProgressParams extends FetchSignal {
  readonly clientId: string;
}

export function getClientPackageProgress(
  params: GetClientPackageProgressParams,
): Promise<GetClientPackageProgressData> {
  return kmiFetch<GetClientPackageProgressData>('get-client-package-progress', {
    method: 'GET',
    signal: params.signal,
    query: { client_id: params.clientId },
  });
}

export interface RecordPackageServiceDeliveryOptions extends FetchSignal {
  readonly input: RecordPackageServiceDeliveryInput;
}

export function recordPackageServiceDelivery(
  options: RecordPackageServiceDeliveryOptions,
): Promise<RecordPackageServiceDeliveryData> {
  const body: Record<string, unknown> = {
    assignment_id: options.input.assignment_id,
    service_id: options.input.service_id,
  };
  for (const k of ['quantity', 'delivered_at', 'deliverable_type', 'notes'] as const) {
    if (k in options.input) body[k] = options.input[k] as unknown;
  }
  return kmiFetch<RecordPackageServiceDeliveryData>('record-package-service-delivery', {
    method: 'POST',
    signal: options.signal,
    body,
  });
}

// Task 059 + Task 165 — Website to Onboarding Extractor.
//
// POST starts extraction. The deployed prod EF (Task 093) returns an
// async start envelope (`status: 'started' | 'already_running' |
// 'recently_succeeded'` + `job_id` + ...); the legacy master EF
// returns the synchronous suggestion shape inline. The frontend
// branches on the response at runtime so both work — see
// `useOnboardingExtractionJob` for the consumer.
//
// GET polls the async job by `job_id` or `client_id`. Same EF path
// as POST; the deployed prod EF handles the dispatch internally.

export interface ExtractClientOnboardingFromWebsiteOptions {
  readonly clientId: string;
  /**
   * Optional override URL. When omitted, the backend reads
   * `brand_info.website_url` for the client. The endpoint validates
   * the URL with SSRF guards before fetching.
   */
  readonly url?: string;
  readonly signal?: AbortSignal;
}

/**
 * Trigger AI extraction of brand identity / products / audiences /
 * competitors from the client's brand website.
 *
 * Wraps `POST /functions/v1/extract-client-onboarding-from-website`.
 *
 * Task 165 — the return type is the discriminated union
 * `ExtractClientOnboardingFromWebsiteResponse` because the deployed
 * prod EF returns the async start envelope while the legacy master
 * EF returns the synchronous suggestion shape. Consumers must branch
 * on `'status'` field presence + value (see
 * `useOnboardingExtractionJob`).
 */
export function extractClientOnboardingFromWebsite(
  options: ExtractClientOnboardingFromWebsiteOptions,
): Promise<ExtractClientOnboardingFromWebsiteResponse> {
  const body: Record<string, unknown> = { client_id: options.clientId };
  if (options.url !== undefined) body['url'] = options.url;
  return kmiFetch<ExtractClientOnboardingFromWebsiteResponse>(
    'extract-client-onboarding-from-website',
    {
      method: 'POST',
      signal: options.signal,
      body,
    },
  );
}

export interface GetClientOnboardingExtractionStatusOptions extends FetchSignal {
  /**
   * Either `client_id` (returns the latest job for the client) or
   * `job_id` (returns one specific row). When both are present the
   * server uses `job_id`. Matches the
   * `getClientStrategyGenerationStatus` semantics.
   */
  readonly client_id?: string;
  readonly job_id?: string;
}

/**
 * Task 165 — poll the status of an onboarding extraction job. Wraps
 * `GET /functions/v1/extract-client-onboarding-from-website`. The
 * deployed prod EF (Task 093) handles the GET dispatch. The legacy
 * master EF does NOT — calling this against a legacy deployment
 * returns 400 `validation_error`, which `useOnboardingExtractionJob`
 * tolerates by relying on the sync POST response shape instead.
 */
export function getClientOnboardingExtractionStatus(
  options: GetClientOnboardingExtractionStatusOptions,
): Promise<GetClientOnboardingExtractionStatusData> {
  const query: Record<string, string> = {};
  if (options.job_id) query['job_id'] = options.job_id;
  if (options.client_id) query['client_id'] = options.client_id;
  return kmiFetch<GetClientOnboardingExtractionStatusData>(
    'extract-client-onboarding-from-website',
    { method: 'GET', signal: options.signal, query },
  );
}

// Task 060 + Task 163 — Apply onboarding suggestions.
//
// The wizard auto-calls `applyClientOnboardingSuggestion` right after
// extraction with `overwrite_existing: false` so AI values fill empty
// fields in place. Task 163 removed the standalone suggestion-review
// surface; the list / reject endpoint wrappers were dropped with it
// (the backend EFs `list-client-onboarding-suggestions` and
// `reject-client-onboarding-suggestion` stay deployed for internal
// / audit use).

export interface ApplyClientOnboardingSuggestionOptions extends FetchSignal {
  readonly input: ApplyClientOnboardingSuggestionInput;
}

/**
 * Apply user-approved fields from a pending onboarding suggestion
 * to the canonical onboarding tables. Single transactional write;
 * partial-failure safe. Wraps
 * `POST /functions/v1/apply-client-onboarding-suggestion`.
 */
export function applyClientOnboardingSuggestion(
  options: ApplyClientOnboardingSuggestionOptions,
): Promise<ApplyClientOnboardingSuggestionData> {
  return kmiFetch<ApplyClientOnboardingSuggestionData>('apply-client-onboarding-suggestion', {
    method: 'POST',
    signal: options.signal,
    body: options.input,
  });
}

// Task 061 + Task 085 — AI strategy generation (async).
//
// The POST handler returns immediately with a `job_id` and runs the
// actual generation in a background task (`EdgeRuntime.waitUntil`).
// Callers poll `getClientStrategyGenerationStatus` until the job
// reaches a terminal state (`succeeded` / `failed`).

export interface GenerateClientStrategyAiOptions extends FetchSignal {
  readonly input: GenerateClientStrategyAiInput;
}

/**
 * Start (or resume) a strategy generation run. Returns immediately
 * with a `job_id`; poll `getClientStrategyGenerationStatus(...)`
 * every 5-10 s until `status === 'succeeded' | 'failed'`.
 *
 * Wraps `POST /functions/v1/generate-client-strategy-ai`.
 *
 * Only `mode === 'new_version'` is wired through the async pipeline
 * in Task 085. `mode === 'draft_only'` returns a `validation_error`
 * until a future task wires drafts through a separate surface.
 */
export function generateClientStrategyAi(
  options: GenerateClientStrategyAiOptions,
): Promise<GenerateClientStrategyAiStartData> {
  return kmiFetch<GenerateClientStrategyAiStartData>('generate-client-strategy-ai', {
    method: 'POST',
    signal: options.signal,
    body: options.input,
  });
}

export interface GetClientStrategyGenerationStatusOptions extends FetchSignal {
  /**
   * Either `client_id` (returns the latest job for the client) or
   * `job_id` (returns one specific row). When both are present the
   * server uses `job_id`.
   */
  readonly client_id?: string;
  readonly job_id?: string;
}

/**
 * Poll the status of a strategy generation job. Wraps
 * `GET /functions/v1/generate-client-strategy-ai`.
 */
export function getClientStrategyGenerationStatus(
  options: GetClientStrategyGenerationStatusOptions,
): Promise<GetStrategyGenerationStatusData> {
  const query: Record<string, string> = {};
  if (options.job_id) query['job_id'] = options.job_id;
  if (options.client_id) query['client_id'] = options.client_id;
  return kmiFetch<GetStrategyGenerationStatusData>('generate-client-strategy-ai', {
    method: 'GET',
    signal: options.signal,
    query,
  });
}

// Task 065 — AI strategy section refine.

export interface RefineClientStrategySectionOptions<
  I extends RefineClientStrategySectionInput,
> extends FetchSignal {
  readonly input: I;
}

/**
 * Refine ONE section of an existing active strategy.
 * Wraps `POST /functions/v1/refine-client-strategy-section`.
 *
 * The endpoint enforces preview-then-publish determinism:
 *
 *   - `mode === 'preview'` calls the AI worker, returns the refined
 *     section + warnings + cost + ai_run_id + base_version_id +
 *     base_version_number + instruction_hash + instruction_excerpt
 *     + instruction_length. NO write to `strategy_versions`.
 *   - `mode === 'publish'` does NOT call the AI. The body MUST
 *     carry the exact `refined_section` from a prior preview, plus
 *     the preview run's metadata + base version pinning. The
 *     server re-validates the section shape, verifies the
 *     `preview_ai_run_id` belongs to this caller / client, and
 *     rejects with 409 if the strategy's active version has
 *     advanced past `base_version_id` / `base_version_number`.
 *
 * Caller must hold `ai.run` AND `strategy.version.publish` for both
 * modes.
 *
 * The TypeScript signature is overloaded so the return type
 * narrows on the input's `mode` discriminant — callers receive a
 * `RefineClientStrategySectionPreviewData` for preview inputs and
 * a `RefineClientStrategySectionPublishData` for publish inputs.
 */
export function refineClientStrategySection(
  options: RefineClientStrategySectionOptions<RefineClientStrategySectionPreviewInput>,
): Promise<RefineClientStrategySectionPreviewData>;
export function refineClientStrategySection(
  options: RefineClientStrategySectionOptions<RefineClientStrategySectionPublishInput>,
): Promise<RefineClientStrategySectionPublishData>;
export function refineClientStrategySection(
  options: RefineClientStrategySectionOptions<RefineClientStrategySectionInput>,
): Promise<RefineClientStrategySectionData> {
  return kmiFetch<RefineClientStrategySectionData>('refine-client-strategy-section', {
    method: 'POST',
    signal: options.signal,
    body: options.input,
  });
}

// ---------------------------------------------------------------------
// Task 071 — AI Prompt Blocks (Knowledge Library)
// ---------------------------------------------------------------------

export function listAiPromptBlocks(
  params: ListAiPromptBlocksParams = {},
  opts: FetchSignal = {},
): Promise<ListAiPromptBlocksData> {
  const query: Record<string, string | number> = {};
  if (params.status !== undefined) query['status'] = params.status;
  if (params.scope !== undefined) query['scope'] = params.scope;
  if (params.client_id !== undefined) query['client_id'] = params.client_id;
  if (params.platform !== undefined) query['platform'] = params.platform;
  if (params.industry !== undefined) query['industry'] = params.industry;
  if (params.service_key !== undefined) query['service_key'] = params.service_key;
  if (params.applies_to !== undefined) query['applies_to'] = params.applies_to;
  if (params.limit !== undefined) query['limit'] = params.limit;
  return kmiFetch<ListAiPromptBlocksData>('list-ai-prompt-blocks', {
    method: 'GET',
    signal: opts.signal,
    query,
  });
}

export function createAiPromptBlock(
  input: CreateAiPromptBlockInput,
  opts: FetchSignal = {},
): Promise<CreateAiPromptBlockData> {
  return kmiFetch<CreateAiPromptBlockData>('create-ai-prompt-block', {
    method: 'POST',
    signal: opts.signal,
    body: input,
  });
}

export function updateAiPromptBlock(
  input: UpdateAiPromptBlockInput,
  opts: FetchSignal = {},
): Promise<UpdateAiPromptBlockData> {
  return kmiFetch<UpdateAiPromptBlockData>('update-ai-prompt-block', {
    method: 'POST',
    signal: opts.signal,
    body: input,
  });
}

export function transitionAiPromptBlockStatus(
  input: TransitionAiPromptBlockStatusInput,
  opts: FetchSignal = {},
): Promise<TransitionAiPromptBlockStatusData> {
  return kmiFetch<TransitionAiPromptBlockStatusData>('transition-ai-prompt-block-status', {
    method: 'POST',
    signal: opts.signal,
    body: input,
  });
}

// Re-export the status enum so the page can import a single name.
export type { AiPromptBlockStatus };

// ---------------------------------------------------------------------
// Task 073 — Creative asset upload + attach.
// ---------------------------------------------------------------------

export function createCreativeAssetUpload(
  input: CreateCreativeAssetUploadInput,
  opts: FetchSignal = {},
): Promise<CreateCreativeAssetUploadData> {
  return kmiFetch<CreateCreativeAssetUploadData>('create-creative-asset-upload', {
    method: 'POST',
    signal: opts.signal,
    body: input,
  });
}

export function completeCreativeAssetUpload(
  input: CompleteCreativeAssetUploadInput,
  opts: FetchSignal = {},
): Promise<CompleteCreativeAssetUploadData> {
  return kmiFetch<CompleteCreativeAssetUploadData>('complete-creative-asset-upload', {
    method: 'POST',
    signal: opts.signal,
    body: input,
  });
}

// ---------------------------------------------------------------------
// Task 236 / 237 — Creative Intelligence standalone BULK upload. Two EFs:
// bulk prepare (create-ci-asset-uploads) + bulk complete
// (complete-ci-asset-uploads). No content target, NO match created.
// ---------------------------------------------------------------------

export function createCiAssetUploads(
  input: CreateCiAssetUploadsInput,
  opts: FetchSignal = {},
): Promise<CreateCiAssetUploadsData> {
  return kmiFetch<CreateCiAssetUploadsData>('create-ci-asset-uploads', {
    method: 'POST',
    signal: opts.signal,
    body: input,
  });
}

export function completeCiAssetUploads(
  input: CompleteCiAssetUploadsInput,
  opts: FetchSignal = {},
): Promise<CompleteCiAssetUploadsData> {
  return kmiFetch<CompleteCiAssetUploadsData>('complete-ci-asset-uploads', {
    method: 'POST',
    signal: opts.signal,
    body: input,
  });
}

export function listContentCreativeAssets(
  params: ListContentCreativeAssetsParams,
  opts: FetchSignal = {},
): Promise<ListContentCreativeAssetsData> {
  const query: Record<string, string> = { client_id: params.client_id };
  if (params.content_item_ids && params.content_item_ids.length > 0) {
    query['content_item_ids'] = params.content_item_ids.join(',');
  }
  if (params.content_variant_ids && params.content_variant_ids.length > 0) {
    query['content_variant_ids'] = params.content_variant_ids.join(',');
  }
  return kmiFetch<ListContentCreativeAssetsData>('list-content-creative-assets', {
    method: 'GET',
    signal: opts.signal,
    query,
  });
}

// Task 076 — archive (remove) an attached creative asset from a
// content_item or content_variant. Server returns IDs + booleans
// only; never the storage_key. Frontend should re-fetch
// `listContentCreativeAssets` after success.
export function archiveContentCreativeAsset(
  input: ArchiveContentCreativeAssetInput,
  opts: FetchSignal = {},
): Promise<ArchiveContentCreativeAssetData> {
  return kmiFetch<ArchiveContentCreativeAssetData>('archive-content-creative-asset', {
    method: 'POST',
    signal: opts.signal,
    body: input,
  });
}

/**
 * Browser-side helper: PUT a file to a signed upload URL with the
 * `Content-Type` header that the storage gateway requires. The
 * server-side allow-list and bucket cap reject anything bad — no
 * additional client-side trust needed.
 */
export async function putToSignedUploadUrl(args: {
  readonly url: string;
  readonly file: File | Blob;
  readonly contentType: string;
  readonly signal?: AbortSignal;
}): Promise<void> {
  const res = await fetch(args.url, {
    method: 'PUT',
    headers: { 'Content-Type': args.contentType },
    body: args.file,
    signal: args.signal,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`upload PUT failed (${res.status}): ${text || 'no body'}`);
  }
}

// ── Task 139 / Task 141 — IC-3 paid-media OAuth on the integration-credential layer ──
//
// Three wrappers for the IC-3 EF pair: start + complete (dual-path:
// first-call code-exchange vs selection re-POST). Server-side authority
// + protocol details in:
//   - docs/decisions/2026-05-15-integration-credential-layer.md (IC-1)
//   - tasks/139-ic-3-paid-media-oauth-edge-functions.md
//   - tasks/141-ic-3-fe-meta-oauth-wiring.md
//
// The state token is the CSRF secret. The FE forwards it back to
// `complete-integration-connection-oauth` alongside the provider-returned
// `code` (Meta echoes `?state=` on the redirect). NEVER persist the
// state in localStorage / sessionStorage — only `redirect_back_to`
// is stashed FE-side for the post-success navigation. The state
// itself is server-bound on the `integration_oauth_states` row.

/**
 * Wire marker for the multi-candidate picker error. The IC-3 callback
 * EF returns 400 `validation_error` with `fields._body[0]` set to this
 * literal when the connecting user's Meta login granted access to more
 * than one ad account. The FE callback page pattern-matches on this
 * marker, parses `fields.candidates[0]` as a JSON array, and renders
 * the picker UI.
 */
export const MULTIPLE_SUBJECTS_MARKER = 'multiple_subjects_available';

export interface StartIntegrationConnectionOAuthOptions extends FetchSignal {
  readonly input: StartIntegrationConnectionOAuthInput;
}

export function startIntegrationConnectionOAuth(
  options: StartIntegrationConnectionOAuthOptions,
): Promise<StartIntegrationConnectionOAuthData> {
  return kmiFetch<StartIntegrationConnectionOAuthData>('start-integration-connection-oauth', {
    method: 'POST',
    signal: options.signal,
    body: {
      client_id: options.input.client_id,
      provider: options.input.provider,
      redirect_back_to: options.input.redirect_back_to ?? null,
    },
  });
}

export interface CompleteIntegrationConnectionOAuthFirstCallOptions extends FetchSignal {
  readonly input: CompleteIntegrationConnectionOAuthFirstCallInput;
}

/**
 * First call from the FE callback page. POSTs `{ state, code }`. On
 * success returns `{ connection }`; on multi-candidate the EF returns
 * a 400 `validation_error` carrying the picker payload — caller
 * detects via `KmiApiError.body.fields?._body?.[0] === MULTIPLE_SUBJECTS_MARKER`.
 */
export function completeIntegrationConnectionOAuthFirstCall(
  options: CompleteIntegrationConnectionOAuthFirstCallOptions,
): Promise<CompleteIntegrationConnectionOAuthData> {
  return kmiFetch<CompleteIntegrationConnectionOAuthData>('complete-integration-connection-oauth', {
    method: 'POST',
    signal: options.signal,
    body: {
      state: options.input.state,
      code: options.input.code,
    },
  });
}

export interface CompleteIntegrationConnectionOAuthSelectOptions extends FetchSignal {
  readonly input: CompleteIntegrationConnectionOAuthSelectInput;
}

/**
 * Selection re-POST from the FE callback page after the user picks
 * one candidate. POSTs `{ state, selected_subject_id }`. The IC-3
 * `finalize_*_v1` RPC validates the selection against the stored
 * candidates and the staged credential; on success the EF inserts
 * the integration_connections row and returns `{ connection }`.
 */
export function completeIntegrationConnectionOAuthSelect(
  options: CompleteIntegrationConnectionOAuthSelectOptions,
): Promise<CompleteIntegrationConnectionOAuthData> {
  return kmiFetch<CompleteIntegrationConnectionOAuthData>('complete-integration-connection-oauth', {
    method: 'POST',
    signal: options.signal,
    body: {
      state: options.input.state,
      selected_subject_id: options.input.selected_subject_id,
    },
  });
}

// ── Task 289A: monthly client report ─────────────────────────────────

export interface GetClientMonthlyReportParams extends FetchSignal {
  readonly clientId: string;
  /** `YYYY-MM`. Interpreted in the supplied timezone. */
  readonly month: string;
  /** IANA timezone string. Defaults to `UTC` server-side. */
  readonly timezone?: string;
}

/**
 * Monthly operational report for one client: bucket counts, platform/
 * discipline/format breakdowns and placement rows, computed server-side
 * from calendar_entries (plan) + publish_jobs.completed_at (publish
 * fact) under the CALLER's RLS (no service_role). Agency-only buckets
 * arrive as null for client-category viewers.
 */
export function getClientMonthlyReport(
  params: GetClientMonthlyReportParams,
): Promise<ClientMonthlyReportData> {
  return kmiFetch<ClientMonthlyReportData>('get-client-monthly-report', {
    method: 'GET',
    signal: params.signal,
    query: {
      client_id: params.clientId,
      month: params.month,
      timezone: params.timezone,
    },
  });
}

// ─────────────────────────────────────────────────────────────────────
// Task 298 (Foundation F) — organization commercial standing
//
// Appended as a single contiguous block per the shared-file protocol in
// `tasks/295A-commercial-foundation-and-staging.md` §9.3. Never
// interleave another lane's additions into this block.
// ─────────────────────────────────────────────────────────────────────

/**
 * BOUNDARY 1 — read the caller's OWN organization's commercial standing.
 *
 * Takes NO organization parameter: the endpoint resolves the caller's own
 * organization server-side and can never be aimed at another tenant
 * (295A §6.1.1). It answers in ALL FOUR commercial states for an AGENCY
 * actor, because its entire purpose is telling a locked-out tenant why it
 * is locked out; every client-category actor is refused in every state.
 *
 * Callers MUST distinguish three outcomes and the guard depends on it:
 *   - resolves            → the standing is authoritative;
 *   - `permission_denied` → not an agency actor / no organization. This
 *     is a REFUSAL, not a standing. Form no opinion.
 *   - `not_found` / network failure → THE ENDPOINT IS NOT DEPLOYED YET.
 *     Also form no opinion. See `CommercialStandingGate` for why treating
 *     that as "denied" would be an outage manufactured by a screen.
 */
export function getOrganizationActivationStatus(
  options: FetchSignal = {},
): Promise<OrganizationActivationStatusData> {
  return kmiFetch<OrganizationActivationStatusData>('get-organization-activation-status', {
    method: 'GET',
    signal: options.signal,
  });
}

/** Task 299 boundary 2 — own agency onboarding profile, any agency role. */
export function getAgencyProfile(options: FetchSignal = {}): Promise<GetAgencyProfileData> {
  return kmiFetch<GetAgencyProfileData>('get-agency-profile', {
    method: 'GET',
    signal: options.signal,
  });
}

/** Task 299 boundary 2 write — own agency owner only, sparse patch. */
export function upsertAgencyProfile(
  input: UpsertAgencyProfileInput,
  options: FetchSignal = {},
): Promise<UpsertAgencyProfileData> {
  return kmiFetch<UpsertAgencyProfileData>('upsert-agency-profile', {
    method: 'POST',
    signal: options.signal,
    body: input,
  });
}

export interface ListOrganizationCommercialStateParams extends FetchSignal {
  readonly state?: CommercialState;
  /** Task 307A — one catalogue key, or absent for every plan. */
  readonly planKey?: string;
  /**
   * Task 307A — matched against organization name, legal name and
   * commercial registration number. Bounded to 120 characters on BOTH
   * sides, and its `like` metacharacters are escaped inside the RPC.
   */
  readonly search?: string;
  /** Task 307A — `recent` when absent. Every sort is deterministic. */
  readonly sort?: CommercialListSort;
  /** Server clamps to 1..100. */
  readonly limit?: number;
  readonly offset?: number;
}

/**
 * Operator control plane — list organizations by commercial standing.
 *
 * `system_admin` only, enforced server-side as the first statement of the
 * definer RPC (295A D6). No agency role can call this successfully, and
 * holding `billing.subscription.manage` is irrelevant to it.
 *
 * Task 307A: search / plan filter / sort / pagination are all SERVER-side.
 * Filtering a page in the browser would be a lie the moment the result set
 * exceeds one page, which is the case this endpoint's `total` exists to
 * report.
 */
export function listOrganizationCommercialState(
  params: ListOrganizationCommercialStateParams = {},
): Promise<ListOrganizationCommercialStateData> {
  const search = params.search?.trim();
  const planKey = params.planKey?.trim();
  return kmiFetch<ListOrganizationCommercialStateData>('list-organization-commercial-state', {
    method: 'GET',
    signal: params.signal,
    query: {
      state: params.state,
      plan_key: planKey && planKey.length > 0 ? planKey : undefined,
      search: search && search.length > 0 ? search : undefined,
      sort: params.sort,
      limit: params.limit,
      offset: params.offset,
    },
  });
}

export interface SetOrganizationCommercialStateParams extends FetchSignal {
  readonly organizationId: string;
  readonly action: CommercialAction;
  /** REQUIRED on every action (295A invariant A4). Blank is rejected. */
  readonly reason: string;
}

/**
 * Operator control plane — transition one organization's standing.
 *
 * `system_admin` only. Idempotent: re-issuing an already-applied action
 * returns the current row with 200 and writes no second audit row
 * (invariant A9). `terminated` is absorbing — every action on it returns
 * 409 (invariant A3).
 */
export function setOrganizationCommercialState(
  params: SetOrganizationCommercialStateParams,
): Promise<SetOrganizationCommercialStateData> {
  return kmiFetch<SetOrganizationCommercialStateData>('set-organization-commercial-state', {
    method: 'POST',
    signal: params.signal,
    body: {
      organization_id: params.organizationId,
      action: params.action,
      reason: params.reason,
    },
  });
}

// ─────────────────────────────────────────────────────────────────────
// Task 301 (Lane B) — agency SaaS subscription commercial summary
//
// Appended as a single contiguous block per the shared-file protocol in
// `tasks/295A-commercial-foundation-and-staging.md` §9.3.
// ─────────────────────────────────────────────────────────────────────

/**
 * The agency's OWN subscription to Super Agency (295A §6.4).
 *
 * Requires `billing.read` AND an activated commercial standing, both
 * enforced server-side. An actor without `billing.read` receives 403 on
 * direct navigation even though the nav entry is hidden — hiding the
 * link is UX, the server refusing is the security property
 * (`SECURITY.md §9`).
 *
 * Read-only. There is no companion write endpoint, no checkout call and
 * no payment mutation anywhere in this module: launch is manual
 * activation and bank transfer (295A D1 / CLP-1.1).
 */
export function getAgencyCommercialSummary(
  options: FetchSignal = {},
): Promise<AgencyCommercialSummaryData> {
  return kmiFetch<AgencyCommercialSummaryData>('get-agency-commercial-summary', {
    method: 'GET',
    signal: options.signal,
  });
}

/**
 * Task 303-A — the agency⇄client contract (externally executed PDF).
 *
 * Task file: `tasks/303A-agency-client-contract-v1.md`. Seven wrappers,
 * one per endpoint. Every one of them is a thin transport call: the
 * gates, the projection choice, the storage-key derivation and the PDF
 * proof are all server-side, and none of them can be influenced from
 * here (`SECURITY.md` §9 — hiding a button is not a control).
 *
 * There is no payment wrapper (§2.9), no template wrapper and no signature
 * wrapper (§2.2) — and there is no delete wrapper anywhere in this block,
 * because nothing in this family is ever deleted (§2.6).
 *
 * Task 303-B adds an EIGHTH wrapper below, `acknowledgeContractVersion`.
 * It is the family's only client-initiated write, and it is not a
 * signature, a payment or an approval — see its own docblock. There is
 * deliberately no wrapper to undo it, because there is no path to undo it.
 */
export function getClientContracts(
  params: { readonly clientId: string } & FetchSignal,
): Promise<ClientContractsOverviewData> {
  return kmiFetch<ClientContractsOverviewData>('get-agency-client-contracts', {
    method: 'GET',
    query: { client_id: params.clientId },
    signal: params.signal,
  });
}

/**
 * Task 303-A-B — the SAME endpoint with no client id, which asks for every
 * client the caller is live-assigned to. It is the only form a client of a
 * suspended or terminated agency can send: `me/context` returns that actor
 * no roles, no permissions and no assignments, so it has no client id to
 * put anywhere.
 *
 * IT IS A SEPARATE FUNCTION ON PURPOSE. Making `clientId` optional on the
 * wrapper above would have meant that a refactor which drops the argument —
 * a renamed prop, a `clientId` that is `undefined` because a param name
 * changed — compiles cleanly and silently receives the WIDER cross-client
 * answer, with an identical response type so nothing downstream notices.
 * On the one endpoint family where the projection IS the privacy boundary,
 * widening must be something a caller asks for by name.
 *
 * No client id reaches the wire in either direction (303-A §2.10): the
 * request carries no query parameter and the response carries no id.
 */
export function getMyClientContracts(
  options: FetchSignal = {},
): Promise<ClientContractsOverviewData> {
  return kmiFetch<ClientContractsOverviewData>('get-agency-client-contracts', {
    method: 'GET',
    signal: options.signal,
  });
}

export function createAgencyClientContract(
  input: CreateAgencyClientContractInput,
  options: FetchSignal = {},
): Promise<CreateAgencyClientContractData> {
  return kmiFetch<CreateAgencyClientContractData>('create-agency-client-contract', {
    method: 'POST',
    body: input,
    signal: options.signal,
  });
}

export function createContractUpload(
  input: CreateContractUploadInput,
  options: FetchSignal = {},
): Promise<CreateContractUploadData> {
  return kmiFetch<CreateContractUploadData>('create-agency-client-contract-upload', {
    method: 'POST',
    body: input,
    signal: options.signal,
  });
}

export function completeContractUpload(
  input: CompleteContractUploadInput,
  options: FetchSignal = {},
): Promise<CompleteContractUploadData> {
  return kmiFetch<CompleteContractUploadData>('complete-agency-client-contract-upload', {
    method: 'POST',
    body: input,
    signal: options.signal,
  });
}

export function releaseContractVersion(
  input: { readonly version_id: string },
  options: FetchSignal = {},
): Promise<ReleaseContractVersionData> {
  return kmiFetch<ReleaseContractVersionData>('release-agency-client-contract-version', {
    method: 'POST',
    body: input,
    signal: options.signal,
  });
}

export function setContractArchiveState(
  input: SetContractArchiveStateInput,
  options: FetchSignal = {},
): Promise<SetContractArchiveStateData> {
  return kmiFetch<SetContractArchiveStateData>('set-agency-client-contract-archive-state', {
    method: 'POST',
    body: input,
    signal: options.signal,
  });
}

/**
 * Task 303-B — record that this client user reviewed the released
 * externally executed document.
 *
 * NOT A SIGNATURE, NOT A PAYMENT, NOT AN APPROVAL of the contract's
 * contents or of any future version. The receipt is bound to ONE
 * `document_id`; a replacement version is unacknowledged until it is
 * acknowledged on its own.
 *
 * `ack_text_version` travels so the server can REFUSE a stale bundle, not
 * so the server can trust it: the RPC compares it to its own constant and
 * answers 400 on a mismatch. The value comes from
 * `CONTRACT_ACK_TEXT_VERSION` in `@kmi/contracts`, which is the same
 * literal the migration pins — never from anything the page computes.
 *
 * Idempotent: re-submitting returns the ORIGINAL receipt with
 * `replayed: true` and writes nothing. There is no un-acknowledge wrapper,
 * because there is no un-acknowledge path (`tasks/303B-…md` §10).
 */
export function acknowledgeContractVersion(
  input: AcknowledgeContractVersionInput,
  options: FetchSignal = {},
): Promise<AcknowledgeContractVersionData> {
  return kmiFetch<AcknowledgeContractVersionData>('acknowledge-agency-client-contract', {
    method: 'POST',
    body: input,
    signal: options.signal,
  });
}

/**
 * Returns the PDF BYTES, not a URL.
 *
 * A Supabase signed URL would put the storage key — and with it the
 * bucket, the organization id and the client id — into the address bar,
 * browser history and every proxy log. §2.10 names all four as things the
 * client response never carries, so the Edge Function streams the file
 * and this wrapper reads it as a blob. POST keeps the document id out of
 * request-line logs.
 */
export function getContractDocumentDownload(
  input: { readonly document_id: string },
  options: FetchSignal = {},
): Promise<ContractDocumentDownload> {
  return kmiFetchBinary('get-agency-client-contract-document', {
    method: 'POST',
    body: input,
    signal: options.signal,
    // Task 306A §3.1.b — the Edge Function verifies `%PDF-`, the exact
    // stored byte length and the exact stored SHA-256 before it replies.
    // Declaring the same signature here is what stops the browser from
    // discarding that guarantee and saving whatever a 200 happened to
    // carry under a forced `.pdf` name.
    expectBinary: { contentType: 'application/pdf', magic: '%PDF-' },
  });
}

// ─────────────────────────────────────────────────────────────────────
// Task 306A — the operator's per-organization detail read
//
// Appended as a single contiguous block per the shared-file protocol in
// `tasks/295A-commercial-foundation-and-staging.md` §9.3.
// ─────────────────────────────────────────────────────────────────────

/**
 * Operator control plane — everything needed to JUDGE one organization.
 *
 * `system_admin` only. The Edge Function refuses every tenant actor as its
 * first statement after authentication, and each of the six tables it
 * reads is additionally gated by an `app.is_system_admin()` RLS policy, so
 * the refusal survives any single mistake in the function (306A §1.3).
 *
 * It exists because the LIST endpoint cannot answer "should I activate
 * this one": that is decided by the Task 299 legal/contact profile, by
 * whether that profile is complete, and by the append-only ledger of what
 * was already decided and why. The two endpoints carrying the first two
 * are own-tenant reads and take no organization argument, so an operator —
 * a non-actor with no tenant context — cannot reach them for another
 * tenant even in principle.
 *
 * Read-only. Every transition still goes through
 * {@link setOrganizationCommercialState}, reason-gated on both sides.
 */
export function getOrganizationCommercialDetail(
  params: { readonly organizationId: string } & FetchSignal,
): Promise<OrganizationCommercialDetailData> {
  return kmiFetch<OrganizationCommercialDetailData>('get-organization-commercial-detail', {
    method: 'GET',
    signal: params.signal,
    query: { organization_id: params.organizationId },
  });
}

// ─────────────────────────────────────────────────────────────────────
// Task 307A — manual SaaS plan assignment
//
// Appended as a single contiguous block per the shared-file protocol in
// `tasks/295A-commercial-foundation-and-staging.md` §9.3.
// ─────────────────────────────────────────────────────────────────────

export interface SetOrganizationSubscriptionPlanParams extends FetchSignal {
  readonly organizationId: string;
  /** A key from {@link OrganizationCommercialDetailData.assignable_plans}. */
  readonly planKey: string;
  /** REQUIRED on every assignment. Blank is rejected on both sides. */
  readonly reason: string;
}

/**
 * Operator control plane — assign or change one agency's Super Agency
 * SaaS plan, manually.
 *
 * `system_admin` only, checked as the first statement of the definer RPC
 * and again in the Edge Function. It exists because 295A D1 keeps
 * payments dormant: there is no checkout, so the plan is a decision an
 * operator makes, and a decision with no endpoint gets made with
 * hand-written SQL instead — which is what an append-only ledger exists
 * to prevent.
 *
 * NOT IDEMPOTENT, deliberately, and this is the one place the console
 * differs from {@link setOrganizationCommercialState}. Re-assigning the
 * plan an organization is already on returns
 * `409 conflict / detail: same_plan`; a plan outside the active public
 * catalogue returns `409 conflict / detail: plan_not_assignable`. A
 * standing action is a command an operator may safely reissue; "assign
 * the plan it already has" is a mistake, and recording it would put a
 * reason in the ledger for a change that never happened.
 *
 * THIS IS THE AGENCY'S OWN SUBSCRIPTION TO SUPER AGENCY. It is not the
 * service packages that agency sells to ITS clients, and nothing here
 * touches `client_package_assignments`, a package snapshot or a contract.
 */
export function setOrganizationSubscriptionPlan(
  params: SetOrganizationSubscriptionPlanParams,
): Promise<SetOrganizationSubscriptionPlanData> {
  return kmiFetch<SetOrganizationSubscriptionPlanData>('set-organization-subscription-plan', {
    method: 'POST',
    signal: params.signal,
    body: {
      organization_id: params.organizationId,
      plan_key: params.planKey,
      reason: params.reason,
    },
  });
}

// ── Task 309A: client organic content performance ─────────────────────────
//
// Reads the pre-computed aggregate for one client, one rolling period and
// (optionally) one platform. It never touches a raw daily fact table — client
// users have no RLS branch on those at all.

export interface GetClientOrganicPerformanceParams extends FetchSignal {
  readonly clientId: string;
  /** One of the three rolling windows. Server default: `last_30_days`. */
  readonly periodKind?: OrganicPeriodKindWire;
  /** Omit for the all-platform snapshot. */
  readonly platform?: OrganicPlatformWire | null;
}

/**
 * Organic content performance for one client and period.
 *
 * Returns `status: 'unavailable'` when no snapshot has been computed for that
 * scope — an honest "we have nothing yet", which the UI must render differently
 * from a period whose measurements were genuinely zero.
 */
export function getClientOrganicPerformance(
  params: GetClientOrganicPerformanceParams,
): Promise<ClientOrganicPerformanceData> {
  return kmiFetch<ClientOrganicPerformanceData>('get-client-organic-performance', {
    method: 'GET',
    signal: params.signal,
    query: {
      client_id: params.clientId,
      period_kind: params.periodKind,
      platform: params.platform ?? undefined,
    },
  });
}
